import React, { useState, useContext, useMemo, useCallback, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Skeleton,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  Switch,
  Alert,
  Stack,
  Divider,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Autocomplete,
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  LocalOffer as LocalOfferIcon,
  AccountBalance as AccountBalanceIcon,
  Inventory as InventoryIcon,
  Scale as ScaleIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  QrCode,
  Person,
  Phone,
  Home,
  Payments,
  ShoppingBag,
  Calculate,
  Summarize,
} from "@mui/icons-material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DataContext } from "../../context/DataContext";
import { format } from "date-fns";
import QRScanner from "../../components/QRScanner";
import {
  generateBillHTML,
  getBillStyles,
  parseItemObject,
} from "../../components/billPrintUtils";
import WeightEditDialog from "../../components/WeightEditDialog";
import WeightAdjustmentDialog from "../../components/WeightAdjustmentDialog";
import AdvancePaymentSelectorDialog from "../../components/AdvancePaymentSelectorDialog";
import { useReactDevTools } from "../../context/ReactDevTools";

// Skeleton Components
const CustomerInfoSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Stack direction="row" alignItems="center" spacing={1} mb={3}>
      <Skeleton variant="rounded" width={36} height={36} />
      <Skeleton variant="text" width={200} height={32} />
    </Stack>
    <Stack spacing={2}>
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
    </Stack>
  </Card>
);

const ItemsSectionSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Skeleton variant="text" width={150} height={32} />
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" width={180} height={40} />
        <Skeleton variant="rounded" width={120} height={40} />
      </Stack>
    </Stack>

    <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: "transparent" }}>
      <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} md={3} key={i}>
            <Skeleton variant="rounded" height={40} />
          </Grid>
        ))}
      </Grid>
    </Paper>

    <TableContainer
      component={Paper}
      sx={{ borderRadius: 2, bgcolor: "transparent" }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              "Item",
              "Serial No",
              "Metal",
              "Purity",
              "Weight",
              "Metal Rate",
              "Labour",
              "Price",
              "Actions",
            ].map((col) => (
              <TableCell key={col}>
                <Skeleton variant="text" width={60} height={24} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3].map((row) => (
            <TableRow key={row}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((cell) => (
                <TableCell key={cell}>
                  <Skeleton variant="text" width={80} height={32} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

const GstSectionSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Skeleton variant="text" width={120} height={28} sx={{ mb: 1 }} />
    <Skeleton variant="rounded" width={200} height={42} sx={{ mb: 2 }} />
    <Skeleton variant="rounded" height={56} />
  </Card>
);

const PaymentSectionSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
    <Grid container spacing={2}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={12} md={6} key={i}>
          <Skeleton variant="rounded" height={56} />
        </Grid>
      ))}
    </Grid>
  </Card>
);

const SummarySkeleton = ({ isDark }) => (
  <Card
    sx={{ p: 0, borderRadius: 3, overflow: "hidden", bgcolor: "transparent" }}
  >
    <Box sx={{ p: 3, pb: 2 }}>
      <Skeleton variant="rounded" width={200} height={36} />
    </Box>
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
      {[1, 2, 3].map((i) => (
        <Box key={i} sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={100} height={20} />
          </Stack>
          <Skeleton variant="rounded" height={4} />
        </Box>
      ))}

      <Box sx={{ p: 2, borderRadius: 1, mt: 2 }}>
        <Stack direction="row" justifyContent="space-between">
          <Skeleton variant="text" width={100} height={28} />
          <Skeleton variant="text" width={120} height={32} />
        </Stack>
      </Box>

      <Skeleton variant="text" width={130} height={28} sx={{ my: 2 }} />
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {[1, 2].map((i) => (
          <Grid item xs={6} key={i}>
            <Skeleton variant="rounded" height={80} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={4} key={i}>
            <Skeleton variant="rounded" height={70} />
          </Grid>
        ))}
      </Grid>
    </Box>
  </Card>
);

const ActionButtonsSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Stack direction="row" spacing={2}>
      <Skeleton variant="rounded" height={52} sx={{ flex: 1 }} />
      <Skeleton variant="rounded" height={52} sx={{ flex: 1 }} />
      <Skeleton variant="rounded" height={52} sx={{ flex: 1 }} />
    </Stack>
  </Card>
);

// Validation Schema
const validationSchema = Yup.object({
  customerName: Yup.string().required("Customer name is required"),
  handledBy: Yup.string().required("Seller name is required"),
  gstNumber: Yup.string(),
  panNumber: Yup.string(),
  transactionDate: Yup.date().required("Transaction date is required"),
  globalMetalRate: Yup.number()
    .min(0, "Must be positive")
    .when(['showGST'], {
      is: true,
      then: (schema) => schema.required("Metal rate is required when GST is enabled"),
      otherwise: (schema) => schema
    }),
  newCustomerContact: Yup.string().when(['isNewCustomer'], {
    is: true,
    then: (schema) => schema.required("Contact number is required for new customers"),
    otherwise: (schema) => schema
  }),
  newCustomerAddress: Yup.string(),
  newCustomerDob: Yup.date().nullable(),
  newCustomerAnniversary: Yup.date().nullable(),
  cashPayment: Yup.number().min(0, "Must be positive").default(0),
  onlinePayment: Yup.number().min(0, "Must be positive").default(0),
  chequePayment: Yup.number().min(0, "Must be positive").default(0),
  otherPayment: Yup.number().min(0, "Must be positive").default(0),
  paymentDone: Yup.number().min(0, "Must be positive").default(0),
  discount: Yup.number().min(0, "Must be positive").default(0),
  totalAmount: Yup.number().when(['transactionType'], {
    is: "total",
    then: (schema) => schema.required("Total amount is required").min(0.01, "Must be greater than 0"),
    otherwise: (schema) => schema.min(0, "Must be positive").default(0)
  })
});

const ManualItemDialog = ({
  open,
  onClose,
  onSubmit,
  manualItemDetails,
  setManualItemDetails,
  globalMetalRate
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { companyDetails, grossStock } = useContext(DataContext);

  const [metalTypes, setMetalTypes] = useState([]);
  const [grossStockOptions, setGrossStockOptions] = useState([]);

  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];
      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  useEffect(() => {
    if (grossStock && Array.isArray(grossStock)) {
      setGrossStockOptions(grossStock);
    }
  }, [grossStock]);

  const calculateManualItemPrice = (details) => {
    const weight = Number(parseFloat(details.weight)) || 0;
    const metalRate = Number(parseFloat(details.metalRate)) || 0;
    const makingCharges = Number(parseFloat(details.makingCharges)) || 0;
    return (metalRate + makingCharges) * weight;
  };

  const handleChange = (field, value) => {
    const updated = { ...manualItemDetails, [field]: value };

    if (field === 'grossStockItem' && value) {
      const selectedItem = grossStockOptions.find(item => item.id === value.id);
      if (selectedItem) {
        updated.metal = selectedItem.metal || 'Silver';
      }
    }

    // Check if it's a per-piece item (weight is 0 or empty)
    const isPerPieceItem = !updated.weight || parseFloat(updated.weight) === 0;

    // Recalculate price when relevant fields change
    if (field === 'weight' || field === 'metalRate' || field === 'makingCharges' || field === 'price') {
      if (isPerPieceItem) {
        // For per-piece items, price is set directly
        if (field === 'price') {
          updated.price = parseFloat(value) || 0;
        } else {
          // Keep existing price for per-piece items
          updated.price = updated.price || 0;
        }
      } else {
        // For weight-based items, calculate price
        updated.price = calculateManualItemPrice(updated);
        if (updated.price) {
          updated.price = Math.round(updated.price);
        }
      }
    }

    setManualItemDetails(updated);
  };

  // Update the handleSubmit in ManualItemDialog
  const handleSubmit = () => {
    // Check if it's a per-piece item
    const isPerPieceItem = !manualItemDetails.weight || parseFloat(manualItemDetails.weight) === 0;

    // For per-piece items, price must be provided
    if (isPerPieceItem && (!manualItemDetails.price || parseFloat(manualItemDetails.price) <= 0)) {
      alert("Please enter a price for this per-piece item");
      return;
    }

    onSubmit(manualItemDetails);
    setManualItemDetails({
      name: '',
      metal: 'Silver',
      grossStockItem: null,
      numberOfPieces: '1', // Default to 1 for per-piece items
      metalRate: globalMetalRate || '',
      makingCharges: '',
      weight: '',
      hsnCode: '',
      huid: '',
      price: 0,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        fontWeight: 700,
        background: isDark
          ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
          : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}>
        <AddIcon />
        Add from Gross Stock
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Item Name *"
            value={manualItemDetails.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            size="small"
            required
            placeholder="Enter item name (e.g., Gold Ring, Silver Chain, etc.)"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(
                  isDark ? theme.palette.background.paper : '#ffffff',
                  isDark ? 0.2 : 0.7
                ),
                '&:hover': {
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.8
                  ),
                }
              },
              '& .MuiOutlinedInput-input': {
                color: isDark ? '#e2e8f0' : '#1e293b',
              },
            }}
          />

          <Autocomplete
            options={grossStockOptions}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return `${option.itemName} (${option.metal}) - Stock: ${option.currentStock || 0} pcs, ${option.currentWeight || 0}g`;
            }}
            value={manualItemDetails.grossStockItem || null}
            onChange={(event, newValue) => {
              handleChange('grossStockItem', newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Gross Stock Item (Optional)"
                fullWidth
                size="small"
                placeholder="Search gross stock items..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.2 : 0.7
                    ),
                    '&:hover': {
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : '#ffffff',
                        isDark ? 0.3 : 0.8
                      ),
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    color: isDark ? '#e2e8f0' : '#1e293b',
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body2">{option.itemName}</Typography>
                  <Chip
                    label={`${option.metal} | ${option.currentStock || 0} pcs | ${option.currentWeight || 0}g`}
                    size="small"
                    sx={{ ml: 1, fontSize: '0.7rem' }}
                  />
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />

          <Autocomplete
            options={metalTypes}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return `${option.name} (${option.shortcut})`;
            }}
            value={metalTypes.find(m => m.name === manualItemDetails.metal) || null}
            onChange={(event, newValue) => {
              if (newValue) {
                handleChange('metal', newValue.name);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Metal *"
                fullWidth
                size="small"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.2 : 0.7
                    ),
                    '&:hover': {
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : '#ffffff',
                        isDark ? 0.3 : 0.8
                      ),
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    color: isDark ? '#e2e8f0' : '#1e293b',
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body2">{option.name}</Typography>
                  <Chip
                    label={option.shortcut}
                    size="small"
                    sx={{ ml: 1, fontSize: '0.7rem' }}
                  />
                </Box>
              </li>
            )}
          />

          <TextField
            label="Number of Pieces"
            type="text"
            value={manualItemDetails.numberOfPieces || ''}
            onChange={(e) => handleChange('numberOfPieces', e.target.value)}
            fullWidth
            size="small"
            placeholder="Enter quantity (e.g., 1, 2, 3...)"
            InputProps={{
              endAdornment: <InputAdornment position="end">pcs</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(
                  isDark ? theme.palette.background.paper : '#ffffff',
                  isDark ? 0.2 : 0.7
                ),
                '&:hover': {
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.8
                  ),
                }
              },
              '& .MuiOutlinedInput-input': {
                color: isDark ? '#e2e8f0' : '#1e293b',
              },
            }}
          />

          <TextField
            label="Weight (g) *"
            type="text"
            value={manualItemDetails.weight}
            onChange={(e) => handleChange('weight', e.target.value)}
            fullWidth
            size="small"
            required
            InputProps={{
              endAdornment: <InputAdornment position="end">g</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(
                  isDark ? theme.palette.background.paper : '#ffffff',
                  isDark ? 0.2 : 0.7
                ),
                '&:hover': {
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.8
                  ),
                }
              },
              '& .MuiOutlinedInput-input': {
                color: isDark ? '#e2e8f0' : '#1e293b',
              },
            }}
          />

          <TextField
            label="Metal Rate (per gram) *"
            type="text"
            value={manualItemDetails.metalRate}
            onChange={(e) => handleChange('metalRate', e.target.value)}
            fullWidth
            size="small"
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(
                  isDark ? theme.palette.background.paper : '#ffffff',
                  isDark ? 0.2 : 0.7
                ),
                '&:hover': {
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.8
                  ),
                }
              },
              '& .MuiOutlinedInput-input': {
                color: isDark ? '#e2e8f0' : '#1e293b',
              },
            }}
          />

          <TextField
            label="Making Charges (per gram)"
            type="text"
            value={manualItemDetails.makingCharges}
            onChange={(e) => handleChange('makingCharges', e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(
                  isDark ? theme.palette.background.paper : '#ffffff',
                  isDark ? 0.2 : 0.7
                ),
                '&:hover': {
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.8
                  ),
                }
              },
              '& .MuiOutlinedInput-input': {
                color: isDark ? '#e2e8f0' : '#1e293b',
              },
            }}
          />

          <TextField
            label="HSN Code"
            value={manualItemDetails.hsnCode}
            onChange={(e) => handleChange('hsnCode', e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(
                  isDark ? theme.palette.background.paper : '#ffffff',
                  isDark ? 0.2 : 0.7
                ),
                '&:hover': {
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.8
                  ),
                }
              },
              '& .MuiOutlinedInput-input': {
                color: isDark ? '#e2e8f0' : '#1e293b',
              },
            }}
          />

          <TextField
            label="HUID"
            value={manualItemDetails.huid}
            onChange={(e) => handleChange('huid', e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(
                  isDark ? theme.palette.background.paper : '#ffffff',
                  isDark ? 0.2 : 0.7
                ),
                '&:hover': {
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.8
                  ),
                }
              },
              '& .MuiOutlinedInput-input': {
                color: isDark ? '#e2e8f0' : '#1e293b',
              },
            }}
          />

          <TextField
            label="Price"
            value={manualItemDetails.price ? Math.round(manualItemDetails.price) : 0}
            InputProps={{ readOnly: true }}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                backgroundColor: alpha(
                  isDark ? theme.palette.background.paper : '#ffffff',
                  isDark ? 0.2 : 0.7
                ),
                '&.Mui-focused': {
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.8
                  ),
                }
              },
              '& .MuiOutlinedInput-input': {
                color: isDark ? '#e2e8f0' : '#1e293b',
                fontWeight: 600,
              },
              '& .MuiInputLabel-root': {
                color: isDark ? '#cbd5e1' : '#64748b',
              },
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : '#ffffff',
              isDark ? 0.3 : 0.8
            ),
            color: isDark ? '#cbd5e1' : '#64748b',
            '&:hover': {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : '#ffffff',
                isDark ? 0.5 : 0.9
              ),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!manualItemDetails.name || manualItemDetails.weight <= 0 || manualItemDetails.metalRate <= 0}
          sx={{
            borderRadius: 2,
            background: isDark
              ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
              : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
            '&:hover': {
              background: isDark
                ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                : 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
            },
            '&.Mui-disabled': {
              background: alpha(
                isDark ? '#60A5FA' : '#2563EB',
                isDark ? 0.3 : 0.5
              ),
            }
          }}
        >
          Add Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Simple Delete Confirmation Dialog
const SimpleDeleteDialog = ({ open, onClose, onConfirm, item }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!isProcessing ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: "blur(20px)",
          backgroundColor: alpha(
            isDark ? theme.palette.background.paper : "#ffffff",
            isDark ? 0.3 : 0.6,
          ),
          border: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.2 : 0.3,
          )}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: isDark ? "#e2e8f0" : "#1e293b",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
        }}
      >
        <DeleteIcon color="error" />
        Delete Item
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
          Are you sure you want to delete "{item?.name || "this item"}"?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={isProcessing}
          sx={{
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : "#ffffff",
              isDark ? 0.3 : 0.8,
            ),
            color: isDark ? "#cbd5e1" : "#64748b",
            "&:hover": {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : "#ffffff",
                isDark ? 0.5 : 0.9,
              ),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isProcessing}
          startIcon={
            isProcessing ? <CircularProgress size={20} /> : <DeleteIcon />
          }
          sx={{
            borderRadius: 2,
            background: isDark
              ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
              : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
            "&:hover": {
              background: isDark
                ? "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)"
                : "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
            },
          }}
        >
          {isProcessing ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Weight Adjustment Delete Dialog
const WeightAdjustmentDeleteDialog = ({ open, onClose, onConfirm, item }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [isProcessing, setIsProcessing] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const handleConfirm = async () => {
    if (!isChecked) return;
    setIsProcessing(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setIsChecked(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={!isProcessing ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: "blur(20px)",
          backgroundColor: alpha(
            isDark ? theme.palette.background.paper : "#ffffff",
            isDark ? 0.3 : 0.6,
          ),
          border: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.2 : 0.3,
          )}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: isDark ? "#e2e8f0" : "#1e293b",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
          borderBottom: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.1 : 0.2,
          )}`,
        }}
      >
        <DeleteIcon color="error" />
        Delete Item - Weight Adjustment Warning
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#f8fafc",
                isDark ? 0.4 : 0.5,
              ),
              border: `1px solid ${alpha(
                isDark ? theme.palette.divider : "#e2e8f0",
                isDark ? 0.2 : 0.3,
              )}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: isDark ? "#e2e8f0" : "#1e293b",
              }}
            >
              Item Details:
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                >
                  Item Name:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                  }}
                >
                  {item?.name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                >
                  Weight:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                  }}
                >
                  {item?.weight || 0}g
                </Typography>
              </Grid>
              {item?.huid && (
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                  >
                    HUID:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: isDark ? "#e2e8f0" : "#1e293b",
                    }}
                  >
                    {item.huid}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>

          <Alert
            severity="warning"
            icon={<InfoIcon />}
            sx={{
              borderRadius: 2,
              backgroundColor: alpha(
                theme.palette.warning.main,
                isDark ? 0.2 : 0.1,
              ),
              border: `1px solid ${alpha(theme.palette.warning.main, isDark ? 0.3 : 0.2)}`,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              ⚠️ Weight Adjustment Already Applied
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This item has weight adjustments that have already been saved.
              Deleting this item will:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li>
                <Typography variant="body2">
                  Remove it from the current transaction
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  NOT revert the weight adjustments that were already processed
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  NOT undo stock changes made due to weight adjustments
                </Typography>
              </li>
            </Box>
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{ color: isDark ? "#cbd5e1" : "#475569" }}
              >
                I understand that weight adjustments are already saved and
                cannot be reverted
              </Typography>
            }
          />

          <Typography
            variant="caption"
            sx={{ color: isDark ? "#94a3b8" : "#64748b", textAlign: "center" }}
          >
            This action cannot be undone.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isProcessing}
          sx={{
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : "#ffffff",
              isDark ? 0.3 : 0.8,
            ),
            color: isDark ? "#cbd5e1" : "#64748b",
            "&:hover": {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : "#ffffff",
                isDark ? 0.5 : 0.9,
              ),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isProcessing || !isChecked}
          startIcon={
            isProcessing ? <CircularProgress size={20} /> : <DeleteIcon />
          }
          sx={{
            borderRadius: 2,
            background: isDark
              ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
              : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
            "&:hover": {
              background: isDark
                ? "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)"
                : "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
            },
            "&.Mui-disabled": {
              background: isDark
                ? "linear-gradient(135deg, #7f8c8d 0%, #7f8c8d 100%)"
                : "linear-gradient(135deg, #95a5a6 0%, #95a5a6 100%)",
            },
          }}
        >
          {isProcessing ? "Deleting..." : "Delete Anyway"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Custom Alert Dialog Component
const CustomAlertDialog = ({ open, onClose, title, message, severity, onConfirm }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const getSeverityGradient = () => {
    switch (severity) {
      case 'error': return 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)';
      case 'warning': return 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)';
      case 'info': return 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)';
      default: return 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
    }
  };

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (onConfirm) {
        onConfirm();
      } else {
        onClose();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }, [onConfirm, onClose]);

  useEffect(() => {
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [open, handleKeyDown]);

  return (
    <Dialog
      open={open}
      onClose={!onConfirm ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: "blur(20px)",
          backgroundColor: alpha(
            isDark ? theme.palette.background.paper : "#ffffff",
            isDark ? 0.3 : 0.6,
          ),
          border: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.2 : 0.3,
          )}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: isDark ? "#e2e8f0" : "#1e293b",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
          borderBottom: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.1 : 0.2,
          )}`,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: getSeverityGradient(),
            color: "white",
          }}
        >
          {severity === 'error' && <ErrorIcon fontSize="small" />}
          {severity === 'warning' && <WarningIcon fontSize="small" />}
          {severity === 'info' && <InfoIcon fontSize="small" />}
          {severity === 'success' && <CheckCircleIcon fontSize="small" />}
        </Box>
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography
          sx={{
            color: isDark ? "#cbd5e1" : "#475569",
            whiteSpace: "pre-line"
          }}
        >
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : "#ffffff",
              isDark ? 0.3 : 0.8,
            ),
            color: isDark ? "#cbd5e1" : "#64748b",
            "&:hover": {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : "#ffffff",
                isDark ? 0.5 : 0.9,
              ),
            },
          }}
        >
          {onConfirm ? "Cancel" : "Close"}
        </Button>
        {onConfirm && (
          <Button
            onClick={onConfirm}
            variant="contained"
            autoFocus
            sx={{
              borderRadius: 2,
              background: getSeverityGradient(),
              "&:hover": {
                background: getSeverityGradient(),
                filter: "brightness(0.9)",
              },
            }}
          >
            OK
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const CustomerTransaction = ({ onBack }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const hasShownAdvanceDialogRef = useRef(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const { isSecretMode } = useReactDevTools();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [items, setItems] = useState([]);
  const [transactionType, setTransactionType] = useState("detailed");
  const [customAlert, setCustomAlert] = useState({
    open: false,
    title: "",
    message: "",
    severity: "success",
    onConfirm: null,
  });

  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    itemToDelete: null,
    hasWeightAdjustment: false,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const {
    customers,
    addCustomer,
    customerTransactions,
    stock,
    addCustomerTransactionAndUpdateStock,
    companyDetails,
    applyAdvanceToBill,
    fetchCustomerAdvanceBalance,
    grossStock,
  } = useContext(DataContext);

  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);
  const [finePayments, setFinePayments] = useState([]);
  const [enableFinePayment, setEnableFinePayment] = useState(false);
  const [, setFinePaymentModes] = useState({
    silver: false,
    gold: false,
  });

  // Calculate total fine required (net weight of items without metal rate)
  const totalFineRequired = useMemo(() => {
    if (transactionType !== "detailed") return 0;

    return items.reduce((sum, item) => {
      const weight = parseFloat(item.weight) || 0;
      const metalRate = parseFloat(item.metalRate) || 0;
      const isPerPieceItem = item.isPerPieceItem || (weight === 0 && item.price > 0);

      // Only include items that don't have a metal rate (fine-based items)
      // Skip per-piece items and items with metal rate
      if (isPerPieceItem || metalRate > 0) return sum;

      return sum + weight;
    }, 0);
  }, [items, transactionType]);

  // Calculate total fine paid from all entries
  const totalFinePaid = useMemo(() => {
    return finePayments.reduce((sum, payment) => {
      const weight = parseFloat(payment.weight) || 0;
      const purity = parseFloat(payment.purity) || 0;
      return sum + (weight * purity / 100);
    }, 0);
  }, [finePayments]);

  // Calculate balance fine
  const balanceFine = useMemo(() => {
    return Math.round((totalFineRequired - totalFinePaid) * 1000) / 1000;
  }, [totalFineRequired, totalFinePaid]);

  // Check if any item has zero metal rate (fine-based items exist)
  const hasFineBasedItems = useMemo(() => {
    return items.some(item => {
      const metalRate = parseFloat(item.metalRate) || 0;
      const weight = parseFloat(item.weight) || 0;
      const isPerPieceItem = item.isPerPieceItem || (weight === 0 && item.price > 0);
      return !isPerPieceItem && metalRate === 0 && weight > 0;
    });
  }, [items]);

  // Add a new fine payment entry
  const handleAddFinePayment = useCallback((metalType = 'silver') => {
    const newPayment = {
      id: `fine_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metal: metalType,
      weight: '',
      purity: metalType === 'silver' ? '100' : '100',
      purityUnit: '%',
    };
    setFinePayments(prev => [...prev, newPayment]);
  }, []);

  // Update a fine payment entry
  const handleUpdateFinePayment = useCallback((id, field, value) => {
    setFinePayments(prev =>
      prev.map(payment => {
        if (payment.id === id) {
          return { ...payment, [field]: value };
        }
        return payment;
      })
    );
  }, []);

  // Remove a fine payment entry
  const handleRemoveFinePayment = useCallback((id) => {
    setFinePayments(prev => prev.filter(payment => payment.id !== id));
  }, []);

  // Clear all fine payments
  const handleClearFinePayments = useCallback(() => {
    setFinePayments([]);
  }, []);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      const safeParseJSON = (data, fallback = []) => {
        if (!data) return fallback;
        if (Array.isArray(data)) return data;
        if (typeof data === 'string') {
          try {
            let cleanData = data.trim();
            if (!cleanData || cleanData === 'null' || cleanData === 'undefined') {
              return fallback;
            }
            const parsed = JSON.parse(cleanData);
            return Array.isArray(parsed) ? parsed : fallback;
          } catch (error) {
            try {
              let cleanData = data.trim();
              if (cleanData.startsWith('"') && cleanData.endsWith('"')) {
                cleanData = cleanData.slice(1, -1);
              }
              if (cleanData.startsWith('[') && cleanData.endsWith(']')) {
                cleanData = cleanData.replace(/'/g, '"');
                cleanData = cleanData.replace(/\\"/g, '"');
                const parsed = JSON.parse(cleanData);
                return Array.isArray(parsed) ? parsed : fallback;
              }
              return fallback;
            } catch (secondError) {
              console.error('Error parsing JSON after cleaning:', secondError);
              return fallback;
            }
          }
        }
        return fallback;
      };
      const parsedItems = safeParseJSON(company.items);
      setItemCategories(parsedItems);

      const parsedMetals = safeParseJSON(company.metals);
      setMetalTypes(parsedMetals);
    }
  }, [companyDetails]);

  // State variables
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [showNewCustomerFields, setShowNewCustomerFields] = useState(false);
  const [showGST, setShowGST] = useState(false);
  const [gstMode, setGstMode] = useState("add");
  const [gstType, setGstType] = useState('cgst_sgst');
  const [enableAdvancePayment, setEnableAdvancePayment] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentModes, setPaymentModes] = useState({
    cash: false,
    online: false,
    oldGold: false,
    oldSilver: false,
  });
  const [advancePayments, setAdvancePayments] = useState({});
  const [manualItemName, setManualItemName] = useState("");
  const [manualItemMetal, setManualItemMetal] = useState("");
  const [manualSerial, setManualSerial] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualItemDialogOpen, setManualItemDialogOpen] = useState(false);
  const [isTransactionSaved, setIsTransactionSaved] = useState(false);
  const [manualItemDetails, setManualItemDetails] = useState({
    name: '',
    metal: 'Silver',
    grossStockItem: null,
    numberOfPieces: '',
    metalRate: '',
    makingCharges: '',
    weight: '',
    hsnCode: '',
    huid: '',
    price: 0,
  });
  const [gstBillNo, setGstBillNo] = useState(null);
  const [nonGstBillNo, setNonGstBillNo] = useState(null);
  const [currentBillNo, setCurrentBillNo] = useState(null);
  const [isLoadingBillNo, setIsLoadingBillNo] = useState(false);
  const [usedBillNo, setUsedBillNo] = useState(null);
  const [weightAdjustments, setWeightAdjustments] = useState({});
  const [currentAdjustmentItem, setCurrentAdjustmentItem] = useState(null);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [editingItemWeight, setEditingItemWeight] = useState(null);
  const [weightEditDialogOpen, setWeightEditDialogOpen] = useState(false);
  const [pendingWeightAdjustments, setPendingWeightAdjustments] = useState(null);
  const [advanceSelectorOpen, setAdvanceSelectorOpen] = useState(false);
  const [customerAdvanceBalance, setCustomerAdvanceBalance] = useState(null);
  const [scannedQRCode, setScannedQRCode] = useState("");
  // Continuous scanning mode
  const [isContinuousScanning, setIsContinuousScanning] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null); // { type: 'success'|'error'|'warning', message: '' }
  const [scanCount, setScanCount] = useState(0);
  const qrInputRef = useRef(null);
  const scanFeedbackTimerRef = useRef(null);
  const scanBufferRef = useRef("");           // accumulates fast keystrokes
  const scanBufferTimerRef = useRef(null);    // detects end of scan
  const [transactionDescription, setTransactionDescription] = useState("");

  // Play beep sound for scan feedback
  const playBeep = (type = 'success') => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      if (type === 'success') {
        oscillator.frequency.value = 1200;  // High beep
        gainNode.gain.value = 0.15;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.08);
      } else {
        // Two-tone error sound
        oscillator.frequency.value = 400;
        gainNode.gain.value = 0.2;
        oscillator.start();
        oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.1);
        oscillator.stop(audioContext.currentTime + 0.25);
      }
    } catch (e) {
      console.log('Audio not available');
    }
  };

  // Fetch advance balance when customer is selected
  useEffect(() => {
    const fetchAdvanceBalance = async () => {
      if (selectedCustomer?.id) {
        try {
          const advanceData = await fetchCustomerAdvanceBalance(selectedCustomer.id);
          setCustomerAdvanceBalance(advanceData);
        } catch (error) {
          console.error("Error fetching advance balance:", error);
          setCustomerAdvanceBalance(null);
        }
      } else {
        setCustomerAdvanceBalance(null);
      }
    };
    fetchAdvanceBalance();
  }, [selectedCustomer?.id, fetchCustomerAdvanceBalance]);

  // Open weight edit dialog
  const openWeightEditDialog = useCallback((item) => {
    const originalStockWeight = item.itemObject?.weight || item.weight;
    setEditingItemWeight({
      id: item.id,
      name: item.name,
      originalStockWeight: originalStockWeight,
      currentWeight: item.weight,
    });
    setWeightEditDialogOpen(true);
  }, []);

  // Handle weight change confirmation
  const handleWeightChangeConfirm = useCallback(
    (newWeight) => {
      if (!editingItemWeight) return;

      const originalStockWeight = editingItemWeight.originalStockWeight;

      setItems((prev) =>
        prev.map((item) => {
          if (item.id === editingItemWeight.id) {
            return { ...item, weight: newWeight };
          }
          return item;
        }),
      );

      const fullItem = items.find((item) => item.id === editingItemWeight.id);

      setPendingWeightAdjustments((prev) => ({
        ...prev,
        [editingItemWeight.id]: {
          originalWeight: originalStockWeight,
          newWeight: newWeight,
          item: fullItem,
          needsAdjustment: true,
        },
      }));

      setCurrentAdjustmentItem({
        ...fullItem,
        originalWeight: originalStockWeight,
        newWeight: newWeight,
      });
      setAdjustmentDialogOpen(true);
      setEditingItemWeight(null);
    },
    [editingItemWeight, items],
  );

  // Replace the updateItemField function with this updated version
  const updateItemField = useCallback(
    (id, field, value) => {
      console.log(`Updating item ${id}: ${field} = ${value}`);

      setItems((prev) =>
        prev.map((item) => {
          if (item.id === id) {
            // Create the updated item with the new field value
            const updatedItem = { ...item, [field]: value };

            // In updateItemField, modify the price calculation section
            if (
              field === "metalRate" ||
              field === "makingCharges" ||
              field === "weight" ||
              field === "price"
            ) {
              const weight = parseFloat(updatedItem.weight) || 0;
              const metalRate = parseFloat(updatedItem.metalRate) || 0;
              const makingCharges = parseFloat(updatedItem.makingCharges) || 0;

              const isPerPieceItem = weight === 0 && updatedItem.price > 0;

              // NEW: Check if this is a fine-based item (no metal rate, only labour)
              const isFineBasedItem = metalRate === 0 && weight > 0 && !isPerPieceItem;

              if (field === "price") {
                const price = parseFloat(value) || 0;

                if (isPerPieceItem) {
                  updatedItem.price = Math.round(price);
                  updatedItem.makingCharges = 0;
                  updatedItem.metalRate = 0;
                } else if (isFineBasedItem) {
                  // For fine-based items, price = labour × weight
                  // When user edits price directly, calculate makingCharges from it
                  if (weight > 0) {
                    updatedItem.makingCharges = parseFloat((price / weight).toFixed(2));
                    updatedItem.price = Math.round(price);
                  }
                } else if (weight > 0 && metalRate > 0) {
                  const calculatedMakingCharges = (price / weight) - metalRate;
                  updatedItem.makingCharges = calculatedMakingCharges > 0 ? parseFloat(calculatedMakingCharges.toFixed(2)) : 0;

                  if (showGST && gstMode === "adjust") {
                    const adjustedMakingCharges = (price / (1.03 * weight)) - metalRate;
                    updatedItem.makingCharges = adjustedMakingCharges > 0 ? parseFloat(adjustedMakingCharges.toFixed(2)) : 0;
                  }

                  updatedItem.price = Math.round(price);
                }
              } else {
                if (isPerPieceItem) {
                  updatedItem.makingCharges = 0;
                  updatedItem.metalRate = 0;
                  updatedItem.price = item.price || 0;
                } else if (isFineBasedItem) {
                  // Fine-based item: price = labour × weight (no metal rate)
                  const calculatedPrice = makingCharges * weight;
                  updatedItem.price = Math.round(calculatedPrice);
                  updatedItem.metalRate = 0; // Ensure metal rate stays 0
                } else if (weight > 0) {
                  const calculatedPrice = (metalRate + makingCharges) * weight;
                  updatedItem.price = Math.round(calculatedPrice);
                }
              }

              if (field === "makingCharges" || field === "metalRate" || field === "price") {
                updatedItem.currentMakingCharges = parseFloat(updatedItem.makingCharges) || 0;
                updatedItem.currentPrice = updatedItem.price;
                updatedItem.manuallyAdjusted = true;
              }
            }

            return updatedItem;
          }
          return item;
        }),
      );
    },
    [showGST, gstMode],
  );

  // Handle manual weight adjustment
  const handleManualWeightAdjustment = useCallback((item) => {
    setCurrentAdjustmentItem(item);
    setAdjustmentDialogOpen(true);
  }, []);

  // Handle adjustment completion
  const handleAdjustmentComplete = useCallback(
    (result) => {
      if (!currentAdjustmentItem) return;

      setItems((prev) =>
        prev.map((item) => {
          if (item.id === currentAdjustmentItem.id) {
            return {
              ...item,
              weightAdjusted: true,
              weightAdjustmentData: {
                ...item.weightAdjustmentData,
                ...result,
                adjustedAt: new Date().toISOString(),
              },
              adjustmentApplied: true,
            };
          }
          return item;
        }),
      );

      setWeightAdjustments((prev) => ({
        ...prev,
        [currentAdjustmentItem.id]: {
          item: currentAdjustmentItem,
          adjustment: result,
        },
      }));

      setCurrentAdjustmentItem(null);
      alert("Weight adjustment applied successfully!");
    },
    [currentAdjustmentItem],
  );

  // Handle skip adjustment
  const handleSkipAdjustment = useCallback(() => {
    if (!currentAdjustmentItem) return;

    setItems((prev) =>
      prev.map((item) => {
        if (item.id === currentAdjustmentItem.id) {
          return {
            ...item,
            weightAdjusted: false,
            adjustmentSkipped: true,
          };
        }
        return item;
      }),
    );

    setCurrentAdjustmentItem(null);
    alert(
      "Weight adjustment skipped. Item will be saved with new weight without stock adjustment.",
    );
  }, [currentAdjustmentItem]);

  // Formik initialization
  const formik = useFormik({
    initialValues: {
      customerName: "",
      handledBy: "",
      gstNumber: "",
      panNumber: "",
      transactionDate: new Date(),
      globalMetalRate: "",
      newCustomerContact: "",
      newCustomerAddress: "",
      newCustomerDob: null,
      newCustomerAnniversary: null,
      cashPayment: "",
      onlinePayment: "",
      chequePayment: "",
      otherPayment: "",
      paymentDone: 0,
      discount: "",
      totalAmount: 0,
      isNewCustomer: false,
      showGST: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
  });

  const calculateItemPrice = useCallback((weight, metalRate, makingCharges, adjustForGST = false, targetAmount = null) => {
    const weightNum = parseFloat(weight) || 0;
    const metalRateNum = parseFloat(metalRate) || 0;
    const makingChargesNum = parseFloat(makingCharges) || 0;

    let price = (metalRateNum + makingChargesNum) * weightNum;

    if (adjustForGST && targetAmount && metalRateNum > 0 && weightNum > 0) {
      const adjustedMakingCharges = (targetAmount / (1.03 * weightNum)) - metalRateNum;
      const newPrice = (metalRateNum + adjustedMakingCharges) * weightNum;
      return {
        price: Math.round(newPrice),
        adjustedMakingCharges: parseFloat(adjustedMakingCharges.toFixed(2))
      };
    }

    return Math.round(price);
  }, []);

  // Core function: process a QR code and add item (used by both manual & auto scan)
  const processQRCodeAdd = useCallback((qrValue, showAlerts = false) => {
    const scannedQR = qrValue?.trim();
    if (!scannedQR) {
      return { success: false, reason: 'empty', message: 'Empty QR code' };
    }

    // Find item in stock by QR key
    const scannedItem = stock?.find(item =>
      item.qr?.toString().trim() === scannedQR
    );

    if (!scannedItem) {
      return {
        success: false,
        reason: 'not_found',
        message: `Item not found: ${scannedQR}`
      };
    }

    // Check if item is already sold
    if (scannedItem?.sold === "Yes") {
      return {
        success: false,
        reason: 'sold',
        message: `"${scannedItem.name}" already sold`
      };
    }

    // Check if item is already added to current bill (by QR or item id)
    const alreadyAdded = items.some(item =>
      (item.itemObject && item.itemObject.id === scannedItem.id) ||
      (item.qr && item.qr === scannedItem.qr)
    );

    if (alreadyAdded) {
      return {
        success: false,
        reason: 'duplicate',
        message: `"${scannedItem.name}" already added`
      };
    }

    const weight = parseFloat(scannedItem.weight) || 0;
    const globalMetalRate = parseFloat(formik.values.globalMetalRate) || 0;
    const metalRate = globalMetalRate || 0;
    const makingCharges = parseFloat(scannedItem.makingCharges) || 0;

    // Check if it's a per-piece item (weight is 0 but has a price)
    const isPerPieceItem = weight === 0 && scannedItem.price > 0;

    let price = 0;
    let adjustedMakingCharges = makingCharges;

    if (isPerPieceItem) {
      price = parseFloat(scannedItem.price) || 0;
      adjustedMakingCharges = 0;
    } else {
      price = (metalRate + makingCharges) * weight;

      if (showGST && gstMode === "adjust") {
        const calculation = calculateItemPrice(
          weight,
          metalRate,
          makingCharges,
          true,
          price
        );

        if (typeof calculation === 'object') {
          price = calculation.price;
          adjustedMakingCharges = calculation.adjustedMakingCharges;
        }
      }
    }

    const newItem = {
      id: Date.now() + Math.random(),
      name: scannedItem.name || '',
      serialNo: scannedItem.serialNo || '',
      metal: scannedItem.metal || '',
      melting: scannedItem.melting || scannedItem.ct || '',
      weight: weight,
      metalRate: isPerPieceItem ? 0 : metalRate,
      makingCharges: isPerPieceItem ? 0 : adjustedMakingCharges,
      price: Math.round(price),
      currentMakingCharges: isPerPieceItem ? 0 : makingCharges,
      currentPrice: isPerPieceItem ? price : (metalRate + makingCharges) * weight,
      gstAdjusted: showGST && gstMode === "adjust" && !isPerPieceItem,
      itemObject: scannedItem,
      qr: scannedItem?.qr || null,
      isPerPieceItem: isPerPieceItem,
      numberOfPieces: 1,
    };

    setItems(prev => [...prev, newItem]);

    if (showAlerts) {
      let message = `Item "${scannedItem.name}" added successfully!`;
      if (isPerPieceItem) {
        message += ` (Per Piece - ₹${Math.round(price)} each)`;
      } else if (newItem.gstAdjusted) {
        message += `\nMaking charges adjusted from ${makingCharges.toFixed(2)} to ${adjustedMakingCharges.toFixed(2)} to include GST`;
      }
      alert(message);
    }

    return {
      success: true,
      item: scannedItem,
      message: `✓ ${scannedItem.name}`
    };
  }, [
    stock,
    items,
    formik.values.globalMetalRate,
    showGST,
    gstMode,
    calculateItemPrice,
  ]);

  // Manual "Add from QR" button handler
  const handleQRCodeAdd = useCallback(() => {
    const result = processQRCodeAdd(scannedQRCode, true);
    if (!result.success && result.reason === 'not_found') {
      alert(`Item with QR code "${scannedQRCode}" not found in inventory`);
    } else if (!result.success && result.reason === 'sold') {
      alert('This item has already been sold and cannot be added to the bill');
    } else if (!result.success && result.reason === 'duplicate') {
      alert('Item already added to this transaction');
    }
    setScannedQRCode("");
  }, [scannedQRCode, processQRCodeAdd]);

  // Show feedback banner briefly
  const showScanFeedback = useCallback((type, message) => {
    if (scanFeedbackTimerRef.current) {
      clearTimeout(scanFeedbackTimerRef.current);
    }
    setScanFeedback({ type, message });
    scanFeedbackTimerRef.current = setTimeout(() => {
      setScanFeedback(null);
    }, 2500);
  }, []);

  // Handle each completed scan (from hardware scanner)
  const handleAutoScanDetected = useCallback((qrValue) => {
    const result = processQRCodeAdd(qrValue, false);

    if (result.success) {
      playBeep('success');
      setScanCount(prev => prev + 1);
      showScanFeedback('success', result.message);
    } else {
      playBeep('error');
      showScanFeedback('error', result.message);
    }

    // Clear input & refocus for next scan
    setScannedQRCode("");
    if (qrInputRef.current) {
      qrInputRef.current.value = "";
      qrInputRef.current.focus();
    }
  }, [processQRCodeAdd, showScanFeedback]);

  // Start continuous scanning mode
  const startContinuousScanning = useCallback(() => {
    setIsContinuousScanning(true);
    setScanCount(0);
    setScannedQRCode("");
    setScanFeedback(null);
    scanBufferRef.current = "";

    // Focus input after render
    setTimeout(() => {
      if (qrInputRef.current) {
        qrInputRef.current.focus();
      }
    }, 100);
  }, []);

  // Stop continuous scanning mode
  const stopContinuousScanning = useCallback(() => {
    setIsContinuousScanning(false);
    setScannedQRCode("");
    if (scanFeedbackTimerRef.current) {
      clearTimeout(scanFeedbackTimerRef.current);
    }
    if (scanBufferTimerRef.current) {
      clearTimeout(scanBufferTimerRef.current);
    }
    setScanFeedback(null);
    setScanCount(0);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (scanFeedbackTimerRef.current) clearTimeout(scanFeedbackTimerRef.current);
      if (scanBufferTimerRef.current) clearTimeout(scanBufferTimerRef.current);
    };
  }, []);

  // Keyboard handler for continuous scanning
  // iBall BST300BT types the QR + Enter. We use Enter as the terminator.
  const handleScanInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();

      const value = scanBufferRef.current || scannedQRCode;
      if (value && value.trim()) {
        handleAutoScanDetected(value.trim());
      }
      scanBufferRef.current = "";
      return;
    }

    // Append printable chars to buffer for fast multi-char input
    if (e.key.length === 1) {
      scanBufferRef.current += e.key;
      // Auto-flush if scanner doesn't send Enter (after 300ms of no input)
      if (scanBufferTimerRef.current) {
        clearTimeout(scanBufferTimerRef.current);
      }
      scanBufferTimerRef.current = setTimeout(() => {
        const value = scanBufferRef.current;
        if (value && value.trim() && isContinuousScanning) {
          handleAutoScanDetected(value.trim());
        }
        scanBufferRef.current = "";
      }, 300);
    }
  }, [scannedQRCode, isContinuousScanning, handleAutoScanDetected]);

  const handleUseAdvance = useCallback((usageData) => {
    const { usageAmounts, totalAmount } = usageData;

    setEnableAdvancePayment(true);

    const newPaymentModes = { ...paymentModes };
    Object.keys(usageAmounts).forEach(mode => {
      if (usageAmounts[mode] > 0) {
        newPaymentModes[mode] = true;
      }
    });
    setPaymentModes(newPaymentModes);

    const updatedAdvancePayments = {};
    Object.entries(usageAmounts).forEach(([mode, amount]) => {
      if (amount > 0) {
        updatedAdvancePayments[mode] = amount.toString();
      }
    });

    setAdvancePayments(updatedAdvancePayments);

    setSnackbar({
      open: true,
      message: `₹${totalAmount.toFixed(2)} advance payment applied successfully!`,
      severity: 'success'
    });
  }, [paymentModes]);

  // Check for advance balance when customer is selected
  useEffect(() => {
    const checkAdvanceBalance = async () => {
      if (selectedCustomer?.id && !isTransactionSaved) {
        if (hasShownAdvanceDialogRef.current !== selectedCustomer.id) {
          try {
            const advanceData = await fetchCustomerAdvanceBalance(selectedCustomer.id);
            setCustomerAdvanceBalance(advanceData);

            if (advanceData && advanceData.total > 0) {
              setAdvanceSelectorOpen(true);
              hasShownAdvanceDialogRef.current = selectedCustomer.id;
            }
          } catch (error) {
            console.error("Error checking advance balance:", error);
          }
        }
      } else if (!selectedCustomer?.id) {
        hasShownAdvanceDialogRef.current = false;
      }
    };

    checkAdvanceBalance();
  }, [selectedCustomer?.id, isTransactionSaved, fetchCustomerAdvanceBalance]);

  // Helper functions
  const getLastCustomerTransaction = useCallback(() => {
    if (!customerTransactions || customerTransactions.length === 0) {
      return { gst: null, nonGst: null };
    }

    let lastGst = null;
    let lastNonGst = null;

    for (let i = customerTransactions.length - 1; i >= 0; i--) {
      const transaction = customerTransactions[i];
      if (transaction.transactionDetails) {
        const gstFlag = transaction.transactionDetails.gstApplicable;
        if (gstFlag === true && !lastGst) lastGst = transaction;
        if (gstFlag === false && !lastNonGst) lastNonGst = transaction;
      }
      if (lastGst && lastNonGst) break;
    }

    return { gst: lastGst, nonGst: lastNonGst };
  }, [customerTransactions]);

  // Fetch last bill numbers
  const fetchLastBillNumbers = useCallback(async () => {
    setIsLoadingBillNo(true);
    try {
      const lastTransaction = getLastCustomerTransaction();
      let lastGstNo = 0;
      let lastNonGstNo = 0;

      if (lastTransaction?.gst?.transactionDetails?.billNo) {
        lastGstNo = parseInt(lastTransaction.gst.transactionDetails.billNo) || 0;
      }

      if (lastTransaction?.nonGst?.transactionDetails?.billNo) {
        lastNonGstNo = parseInt(lastTransaction.nonGst.transactionDetails.billNo) || 0;
      }

      setGstBillNo(lastGstNo);
      setNonGstBillNo(lastNonGstNo);

      if (showGST) {
        setCurrentBillNo(lastGstNo + 1);
      } else {
        setCurrentBillNo(lastNonGstNo + 1);
      }
    } catch (error) {
      console.error("Error fetching bill numbers:", error);
      setGstBillNo(0);
      setNonGstBillNo(0);
      setCurrentBillNo(1);
    } finally {
      setIsLoadingBillNo(false);
    }
  }, [getLastCustomerTransaction, showGST]);

  // Load bill numbers when component mounts or GST status changes
  useEffect(() => {
    fetchLastBillNumbers();
  }, [fetchLastBillNumbers, showGST]);

  const calculations = useMemo(() => {
    let subtotal = 0;
    let totalNetWeight = 0;

    if (transactionType === "detailed") {
      subtotal = items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
      totalNetWeight = items.reduce((sum, item) => {
        const weight = parseFloat(item.weight) || 0;
        const isPerPieceItem = item.isPerPieceItem || (weight === 0 && item.price > 0);
        return sum + (isPerPieceItem ? 0 : weight);
      }, 0);
    } else {
      subtotal = parseFloat(formik.values.totalAmount) || 0;
    }

    subtotal = Math.round(subtotal);

    let sub = subtotal;
    let discountAmount = Math.round(parseFloat(formik.values.discount) || 0);
    let cgst = 0, sgst = 0, igst = 0, grandTotal = subtotal;

    if (showGST) {
      if (gstType === 'igst') {
        igst = Math.round(subtotal * 0.03);
        grandTotal = subtotal + igst - discountAmount;
      } else {
        cgst = Math.round(subtotal * 0.015);
        sgst = Math.round(subtotal * 0.015);
        grandTotal = subtotal + cgst + sgst - discountAmount;
      }
    } else {
      grandTotal = subtotal - discountAmount;
    }

    grandTotal = Math.round(grandTotal);

    // NEW: Calculate fine required and balance
    const fineRequired = totalFineRequired;
    const finePaid = totalFinePaid;
    const fineBalance = Math.round((fineRequired - finePaid) * 1000) / 1000;

    return {
      subtotal: sub,
      cgst,
      sgst,
      igst,
      grandTotal,
      totalNetWeight,
      fineRequired,
      finePaid,
      fineBalance,
    };
  }, [items, formik.values.totalAmount, formik.values.discount, showGST, gstType, transactionType, totalFineRequired, totalFinePaid]);

  const advanceTotal = useMemo(
    () => Object.values(advancePayments).reduce((sum, val) => sum + (parseFloat(val) || 0), 0),
    [advancePayments]
  );

  // Update totalPayments to consider fine payments
  const totalPayments = useMemo(() => {
    const cashPayments =
      (parseFloat(formik.values.cashPayment) || 0) +
      (parseFloat(formik.values.onlinePayment) || 0) +
      (parseFloat(formik.values.chequePayment) || 0) +
      (parseFloat(formik.values.otherPayment) || 0) +
      advanceTotal;

    return Math.round(cashPayments);
  }, [
    formik.values.cashPayment,
    formik.values.onlinePayment,
    formik.values.chequePayment,
    formik.values.otherPayment,
    advanceTotal,
  ]);

  // Pending amount (cash pending)
  const pendingAmount = useMemo(
    () => Math.round(calculations.grandTotal - totalPayments),
    [calculations.grandTotal, totalPayments]
  );

  const { setFieldValue } = formik;

  // Auto-adjust advance payments
  useEffect(() => {
    if (!enableAdvancePayment) return;

    let needsUpdate = false;
    const updatedAdvancePayments = { ...advancePayments };

    if (customerAdvanceBalance?.total > 0) {
      Object.keys(updatedAdvancePayments).forEach(mode => {
        const currentValue = parseFloat(updatedAdvancePayments[mode]) || 0;
        const availableBalance = customerAdvanceBalance?.byMode?.[mode] || 0;

        if (availableBalance > 0 && currentValue > availableBalance) {
          updatedAdvancePayments[mode] = availableBalance.toString();
          needsUpdate = true;
          setSnackbar({
            open: true,
            message: `Adjusted ${mode} advance to available balance: ₹${availableBalance.toFixed(2)}`,
            severity: 'warning'
          });
        }
      });
    }

    const totalAdvance = Object.values(updatedAdvancePayments).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const otherPayments = (parseFloat(formik.values.cashPayment) || 0) +
      (parseFloat(formik.values.onlinePayment) || 0) +
      (parseFloat(formik.values.chequePayment) || 0) +
      (parseFloat(formik.values.otherPayment) || 0);

    const totalAllPayments = otherPayments + totalAdvance;

    if (totalAllPayments > calculations.grandTotal && calculations.grandTotal > 0) {
      const excess = totalAllPayments - calculations.grandTotal;
      if (totalAdvance > 0) {
        const reductionFactor = (totalAdvance - excess) / totalAdvance;
        Object.keys(updatedAdvancePayments).forEach(mode => {
          const currentValue = parseFloat(updatedAdvancePayments[mode]) || 0;
          const newValue = parseFloat((currentValue * reductionFactor).toFixed(2));
          updatedAdvancePayments[mode] = newValue > 0 ? newValue.toString() : "0";
        });
        needsUpdate = true;
        setSnackbar({
          open: true,
          message: `Adjusted advance payments to not exceed bill amount`,
          severity: 'warning'
        });
      }
    }

    if (needsUpdate) {
      setAdvancePayments(updatedAdvancePayments);
    }
  }, [advancePayments, customerAdvanceBalance, calculations.grandTotal, formik.values.cashPayment, formik.values.onlinePayment, formik.values.chequePayment, formik.values.otherPayment, enableAdvancePayment]);

  // Update paymentDone when totalPayments changes
  useEffect(() => {
    setFieldValue("paymentDone", totalPayments);
  }, [totalPayments, setFieldValue]);

  // Recalculate items when GST mode changes
  useEffect(() => {
    if (items.length === 0 || !formik.values.globalMetalRate) return;

    const globalMetalRate = parseFloat(formik.values.globalMetalRate) || 0;
    if (globalMetalRate === 0) return;

    if (!showGST) {
      const restoredItems = items.map((item) => {
        // If item was manually adjusted, keep its current values
        if (item.manuallyAdjusted) {
          return {
            ...item,
            gstAdjusted: false,
          };
        }
        if (item.currentMakingCharges !== undefined && item.currentPrice !== undefined) {
          return {
            ...item,
            makingCharges: item.currentMakingCharges,
            price: Math.round(item.currentPrice),
            gstAdjusted: false,
          };
        }
        return item;
      });

      const hasChanges = restoredItems.some((item, index) =>
        item.price !== items[index].price
      );

      if (hasChanges) {
        setItems(restoredItems);
      }
      return;
    }

    if (showGST && gstMode === "add") {
      const restoredItems = items.map((item) => {
        // If item was manually adjusted, keep its current values
        if (item.manuallyAdjusted) {
          return {
            ...item,
            gstAdjusted: false,
          };
        }
        if (item.gstAdjusted === true && item.currentMakingCharges !== undefined && item.currentPrice !== undefined) {
          return {
            ...item,
            makingCharges: item.currentMakingCharges,
            price: Math.round(item.currentPrice),
            gstAdjusted: false,
          };
        }
        return item;
      });

      const hasChanges = restoredItems.some((item, index) =>
        item.price !== items[index].price
      );

      if (hasChanges) {
        setItems(restoredItems);
      }
      return;
    }

    if (showGST && gstMode === "adjust") {
      const updatedItems = items.map((item) => {
        // If item was manually adjusted, don't auto-adjust it
        if (item.manuallyAdjusted) {
          return {
            ...item,
            gstAdjusted: false,
          };
        }

        if (!item.weight || !item.metalRate) return item;

        const weight = parseFloat(item.weight) || 0;
        const metalRate = parseFloat(item.metalRate) || 0;

        if (weight === 0 || metalRate === 0) return item;

        let originalMakingCharges = item.currentMakingCharges;
        let originalPrice = item.currentPrice;

        if (originalMakingCharges === undefined || originalPrice === undefined) {
          originalMakingCharges = parseFloat(item.makingCharges) || 0;
          originalPrice = (metalRate + originalMakingCharges) * weight;
        }

        const adjustedMakingCharges = (originalPrice / (1.03 * weight)) - metalRate;
        const adjustedPrice = (metalRate + adjustedMakingCharges) * weight;

        return {
          ...item,
          price: Math.round(adjustedPrice),
          makingCharges: parseFloat(adjustedMakingCharges.toFixed(2)),
          currentMakingCharges: originalMakingCharges,
          currentPrice: originalPrice,
          gstAdjusted: true,
        };
      });

      const hasChanges = updatedItems.some((item, index) =>
        Math.abs(item.makingCharges - items[index].makingCharges) > 0.01
      );

      if (hasChanges) {
        setItems(updatedItems);
      }
    }
  }, [showGST, gstMode, items, formik.values.globalMetalRate]);

  // Event handlers
  const handleQRScan = (qrData) => {
    const scannedQR = qrData.trim();
    const scannedItem = stock?.find(item => item.qr === scannedQR);

    if (!scannedItem) {
      alert(`Item with QR code "${scannedQR}" not found in inventory`);
      return;
    }

    // Check if item is already sold
    if (scannedItem?.sold === "Yes") {
      alert('This item has already been sold and cannot be added to the bill');
      return;
    }

    // Check if item is already added to current bill
    const alreadyAdded = items.some(item =>
      item.itemObject && item.itemObject.id === scannedItem.id
    );

    if (alreadyAdded) {
      alert('Item already added to this transaction');
      return;
    }

    const weight = parseFloat(scannedItem.weight) || 0;
    const metalRate = parseFloat(formik.values.globalMetalRate) || parseFloat(scannedItem.metalRate) || 0;
    const makingCharges = parseFloat(scannedItem.makingCharges) || 0;

    let price = (metalRate + makingCharges) * weight;
    let adjustedMakingCharges = makingCharges;

    if (showGST && gstMode === "adjust") {
      const calculation = calculateItemPrice(
        weight,
        metalRate,
        makingCharges,
        true,
        price
      );

      if (typeof calculation === 'object') {
        price = calculation.price;
        adjustedMakingCharges = calculation.adjustedMakingCharges;
      }
    }

    const newItem = {
      id: Date.now(),
      name: scannedItem.name || '',
      serialNo: scannedItem.serialNo || '',
      metal: scannedItem.metal || '',
      melting: scannedItem.ct || scannedItem?.melting || '',
      weight: weight,
      metalRate: metalRate,
      makingCharges: adjustedMakingCharges,
      price: Math.round(price),
      currentMakingCharges: makingCharges,
      currentPrice: (metalRate + makingCharges) * weight,
      gstAdjusted: showGST && gstMode === "adjust",
      itemObject: scannedItem,
      qr: scannedItem?.qr || null,
    };

    setItems(prev => [...prev, newItem]);
    setScannerOpen(false);

    let message = `Item "${scannedItem.name}" added successfully!`;
    if (newItem.gstAdjusted) {
      message += `\nMaking charges adjusted from ${makingCharges.toFixed(2)} to ${adjustedMakingCharges.toFixed(2)} to include GST`;
    }
    alert(message);
  };

  const filteredCustomerSuggestions = useMemo(() => {
    if (!customerQuery?.trim()) return [];
    const query = customerQuery.toLowerCase().trim();
    return customers.filter(
      (c) =>
        c.name?.toLowerCase().includes(query) ||
        c.contact?.toString().toLowerCase().includes(query)
    );
  }, [customerQuery, customers]);

  useEffect(() => {
    if (filteredCustomerSuggestions.length > 0) {
      setCustomerSuggestions(filteredCustomerSuggestions);
      setIsNewCustomer(false);
      setShowNewCustomerFields(false);
    } else if (customerQuery?.trim()) {
      setIsNewCustomer(true);
      setCustomerSuggestions([{
        id: "new",
        name: `${customerQuery}`,
        isNew: true,
      }]);
    } else {
      setCustomerSuggestions([]);
      setIsNewCustomer(false);
      setShowNewCustomerFields(false);
    }
    setIsLoading(false)
  }, [filteredCustomerSuggestions, customerQuery]);

  const handleCustomerSelect = useCallback(
    (customer) => {
      if (customer.isNew) {
        formik.setFieldValue("customerName", customerQuery);
        formik.setFieldValue("isNewCustomer", true);
        setShowNewCustomerFields(true);
        setIsNewCustomer(true);
        setSelectedCustomer(null);
        formik.setFieldValue("gstNumber", "");
        formik.setFieldValue("panNumber", "");
      } else {
        setSelectedCustomer(customer);
        formik.setFieldValue("customerName", customer.name);
        formik.setFieldValue("gstNumber", customer.gstNo || "");
        formik.setFieldValue("panNumber", customer.panNo || "");
        setIsNewCustomer(false);
        setShowNewCustomerFields(false);
      }
      setCustomerQuery(customer.name);
      setCustomerSuggestions([]);
    },
    [customerQuery, formik]
  );

  const handleAddBySerial = useCallback(() => {
    if (!manualItemName) {
      alert("Please select an item type");
      return;
    }

    if (!manualSerial) {
      alert("Please enter serial number");
      return;
    }

    const globalMetalRate = parseFloat(formik.values.globalMetalRate) || 0;
    // if (globalMetalRate === 0) {
    //   alert("Please set the global metal rate first");
    //   return;
    // }

    const stockItem = stock?.find((item) => {
      const serialMatch = item.serialNo?.toString() === manualSerial?.toString();
      const nameMatch = item.name?.toString() === manualItemName?.toString();
      const metalMatch = manualItemMetal ? item.metal?.toString() === manualItemMetal?.toString() : true;
      const notSold = item.sold === "No";
      return serialMatch && nameMatch && metalMatch && notSold;
    });

    if (stockItem?.sold === "Yes") {
      alert("This item has already been sold and cannot be added to the bill.");
      return;
    }

    // Check if item is already added to current bill
    const alreadyAdded = items.some(item =>
      item.itemObject && item.itemObject.id === stockItem?.id
    );

    if (alreadyAdded) {
      alert("This item is already added to the current transaction.");
      return;
    }

    if (stockItem) {
      const weight = parseFloat(stockItem.weight) || 0;
      const makingCharges = parseFloat(stockItem.makingCharges) || 0;

      // Check if it's a per-piece item (weight is 0 but has a price)
      const isPerPieceItem = weight === 0 && stockItem.price > 0;

      let price = 0;
      let adjustedMakingCharges = makingCharges;

      if (isPerPieceItem) {
        // For per-piece items, use the price directly from stock
        price = parseFloat(stockItem.price) || 0;
        adjustedMakingCharges = 0;
      } else {
        // For weight-based items, calculate price
        price = (globalMetalRate + makingCharges) * weight;

        if (showGST && gstMode === "adjust") {
          const calculation = calculateItemPrice(
            weight,
            globalMetalRate,
            makingCharges,
            true,
            price
          );

          if (typeof calculation === 'object') {
            price = calculation.price;
            adjustedMakingCharges = calculation.adjustedMakingCharges;
          }
        }
      }

      const newItem = {
        id: Date.now(),
        name: `${stockItem.name}`,
        serialNo: stockItem.serialNo || '',
        metal: stockItem.metal || '',
        melting: stockItem.ct || stockItem?.melting || '',
        weight: weight,
        metalRate: isPerPieceItem ? 0 : globalMetalRate,
        makingCharges: isPerPieceItem ? 0 : adjustedMakingCharges,
        price: Math.round(price),
        currentMakingCharges: isPerPieceItem ? 0 : makingCharges,
        currentPrice: isPerPieceItem ? price : (globalMetalRate + makingCharges) * weight,
        gstAdjusted: showGST && gstMode === "adjust" && !isPerPieceItem,
        itemObject: stockItem,
        qr: stockItem?.qr || null,
        isPerPieceItem: isPerPieceItem, // Add flag to identify per-piece items
        numberOfPieces: 1, // Default to 1 for per-piece items
      };

      setItems((prev) => [...prev, newItem]);

      setManualItemName("");
      setManualItemMetal("");
      setManualSerial("");

      let message = `Item "${stockItem.name}" added successfully!`;
      if (isPerPieceItem) {
        message += ` (Per Piece - ₹${Math.round(price)} each)`;
      } else if (newItem.gstAdjusted) {
        message += `\nMaking charges adjusted from ${makingCharges.toFixed(2)} to ${adjustedMakingCharges.toFixed(2)} to include GST`;
      }
      alert(message);
    } else {
      const metalFilter = manualItemMetal ? `, Metal: ${manualItemMetal}` : '';
      alert(`Item not found in stock. Serial: ${manualSerial}, Name: ${manualItemName}${metalFilter}`);
    }
  }, [manualItemName, manualItemMetal, manualSerial, stock, calculateItemPrice, showGST, gstMode, formik.values.globalMetalRate, items]);

  const removeItemRow = useCallback(
    (id) => {
      const itemToDelete = items.find((item) => item.id === id);

      if (!itemToDelete) return;

      const hasWeightAdjustment = !!(
        itemToDelete.weightAdjusted ||
        itemToDelete.adjustmentApplied ||
        itemToDelete.weightAdjustmentData ||
        (pendingWeightAdjustments && pendingWeightAdjustments[id])
      );

      setDeleteConfirmDialog({
        open: true,
        itemToDelete: itemToDelete,
        hasWeightAdjustment: hasWeightAdjustment,
      });
    },
    [items, pendingWeightAdjustments],
  );

  const confirmDeleteItem = useCallback(async () => {
    const { itemToDelete, hasWeightAdjustment } = deleteConfirmDialog;

    if (!itemToDelete) return false;

    try {
      if (hasWeightAdjustment) {
        console.log(`Deleting item ${itemToDelete.id} with weight adjustments. 
                    Weight adjustments were already saved and won't be reverted.`);

        if (
          pendingWeightAdjustments &&
          pendingWeightAdjustments[itemToDelete.id]
        ) {
          const newPendingAdjustments = { ...pendingWeightAdjustments };
          delete newPendingAdjustments[itemToDelete.id];
          setPendingWeightAdjustments(newPendingAdjustments);
        }

        if (weightAdjustments[itemToDelete.id]) {
          const newWeightAdjustments = { ...weightAdjustments };
          delete newWeightAdjustments[itemToDelete.id];
          setWeightAdjustments(newWeightAdjustments);
        }
      }

      setItems((prev) => prev.filter((item) => item.id !== itemToDelete.id));

      let message = `Item "${itemToDelete.name}" removed from transaction.`;
      if (hasWeightAdjustment) {
        message += ` Weight adjustments were already saved and cannot be reverted.`;
      }

      setSnackbar({
        open: true,
        message: message,
        severity: hasWeightAdjustment ? "warning" : "success",
      });

      setDeleteConfirmDialog({
        open: false,
        itemToDelete: null,
        hasWeightAdjustment: false,
      });

      return true;
    } catch (error) {
      console.error("Error deleting item:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete item. Please try again.",
        severity: "error",
      });
      return false;
    }
  }, [
    deleteConfirmDialog,
    pendingWeightAdjustments,
    weightAdjustments,
    setItems,
  ]);

  const getAvailableBalanceDisplay = (mode) => {
    if (!customerAdvanceBalance?.byMode?.[mode]) return null;
    const balance = customerAdvanceBalance.byMode[mode];

    if (balance === 0) {
      return (
        <Chip
          label={`No advance balance`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.65rem', color: isDark ? '#94a3b8' : '#64748b' }}
        />
      );
    }

    return (
      <Chip
        label={`Available: ₹${balance.toFixed(2)}`}
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.65rem', color: 'success.main' }}
      />
    );
  };

  const handleAdvancePaymentChange = useCallback((mode, value) => {
    const numValue = parseFloat(value) || 0;

    if (customerAdvanceBalance?.byMode?.[mode] > 0) {
      const availableBalance = customerAdvanceBalance.byMode[mode];
      if (numValue > availableBalance) {
        setSnackbar({
          open: true,
          message: `Cannot use more than available advance balance for ${mode}. Available: ₹${availableBalance.toFixed(2)}. For manual advance, please use regular payment fields.`,
          severity: 'error'
        });
        return;
      }
    }

    const currentTotalWithoutThis = totalPayments - (parseFloat(advancePayments[mode]) || 0);
    if (currentTotalWithoutThis + numValue > calculations.grandTotal) {
      setSnackbar({
        open: true,
        message: `Total payments (including advance) cannot exceed bill amount of ₹${calculations.grandTotal.toFixed(2)}`,
        severity: 'error'
      });
      return;
    }

    setAdvancePayments((prev) => ({ ...prev, [mode]: value }));
  }, [customerAdvanceBalance, totalPayments, calculations.grandTotal, advancePayments]);

  // Update the handleAddManualItem function
  const handleAddManualItem = async (itemDetails) => {
    const globalMetalRate = parseFloat(formik.values.globalMetalRate) || 0;
    const metalRate = itemDetails.metalRate || globalMetalRate;
    const makingCharges = parseFloat(itemDetails.makingCharges) || 0;
    const weight = parseFloat(itemDetails.weight) || 0;

    // Check if it's a per-piece item (weight is 0)
    const isPerPieceItem = weight === 0;

    let price = 0;
    let adjustedMakingCharges = makingCharges;
    let adjustedPrice = 0;

    if (isPerPieceItem) {
      // For per-piece items, use the price directly
      price = parseFloat(itemDetails.price) || 0;
      adjustedPrice = price;
    } else {
      // For weight-based items, calculate price from weight, metalRate, and makingCharges
      price = (metalRate + makingCharges) * weight;
      adjustedPrice = price;

      if (showGST && gstMode === "adjust") {
        const calculation = calculateItemPrice(
          weight,
          metalRate,
          makingCharges,
          true,
          price
        );

        if (typeof calculation === 'object') {
          adjustedPrice = calculation.price;
          adjustedMakingCharges = calculation.adjustedMakingCharges;
        }
      }
    }

    const newItem = {
      id: Date.now(),
      name: `${itemDetails.name} ${itemDetails?.huid ? `- ${itemDetails.huid}` : ""}`,
      serialNo: '',
      metal: itemDetails.metal || '',
      melting: '',
      weight: weight,
      metalRate: isPerPieceItem ? 0 : metalRate,
      makingCharges: isPerPieceItem ? 0 : adjustedMakingCharges,
      price: Math.round(adjustedPrice),
      currentMakingCharges: isPerPieceItem ? 0 : makingCharges,
      currentPrice: adjustedPrice,
      gstAdjusted: showGST && gstMode === "adjust" && !isPerPieceItem,
      itemObject: null,
      grossStockItem: itemDetails.grossStockItem || null,
      grossStockItemId: itemDetails.grossStockItem?.id || null,
      numberOfPieces: parseFloat(itemDetails.numberOfPieces) || 1, // Default to 1 for per-piece items
      fromGrossStock: !!itemDetails.grossStockItem,
      huid: itemDetails.huid,
      isPerPieceItem: isPerPieceItem, // Add flag to identify per-piece items
      manualItemDetails: {
        ...itemDetails,
        makingCharges: isPerPieceItem ? 0 : adjustedMakingCharges
      },
    };

    setItems(prev => [...prev, newItem]);

    let message = `${itemDetails.name} added successfully!`;
    if (isPerPieceItem) {
      message += ` (Per Piece - ₹${Math.round(adjustedPrice)} each)`;
    } else if (newItem.gstAdjusted) {
      message += ` Making charges adjusted from ${makingCharges.toFixed(2)} to ${adjustedMakingCharges.toFixed(2)} to include GST`;
    }
    if (newItem.fromGrossStock && newItem.grossStockItem) {
      message += `\n📦 Will deduct ${newItem.numberOfPieces || 0} pcs and ${newItem.weight}g from Gross Stock "${newItem.grossStockItem.itemName}"`;
    }
    alert(message);
  };

  // Validate GROSS STOCK availability
  const validateGrossStockAvailability = useCallback(async () => {
    const grossStockIssues = [];
    const grossStockGroups = {};

    for (const item of items) {
      if (item.fromGrossStock && item.grossStockItemId) {
        const grossStockItemId = item.grossStockItemId;

        if (!grossStockGroups[grossStockItemId]) {
          grossStockGroups[grossStockItemId] = {
            item: item.grossStockItem,
            totalPiecesRequested: 0,
            totalWeightRequested: 0,
            items: []
          };
        }

        grossStockGroups[grossStockItemId].totalPiecesRequested += parseFloat(item.numberOfPieces) || 0;
        grossStockGroups[grossStockItemId].totalWeightRequested += parseFloat(item.weight) || 0;
        grossStockGroups[grossStockItemId].items.push(item);
      }
    }

    if (Object.keys(grossStockGroups).length === 0) {
      return [];
    }

    for (const [itemId, group] of Object.entries(grossStockGroups)) {
      const grossItem = grossStock?.find(g => g.id === itemId);

      if (!grossItem) {
        grossStockIssues.push({
          message: `Gross Stock item "${group.item?.itemName || 'Unknown'}" not found in inventory`
        });
        continue;
      }

      const availablePieces = parseFloat(grossItem.currentStock) || 0;
      const availableWeight = parseFloat(grossItem.currentWeight) || 0;
      const requestedPieces = group.totalPiecesRequested;
      const requestedWeight = group.totalWeightRequested;

      let hasIssue = false;
      let issueMessage = `Item: ${grossItem.itemName} (${grossItem.metal})`;

      if (requestedPieces > 0 && requestedPieces > availablePieces) {
        hasIssue = true;
        issueMessage += `\n  📦 Pieces: Requested ${requestedPieces}, Available: ${availablePieces} (Shortage: ${(requestedPieces - availablePieces).toFixed(0)})`;
      }

      if (requestedWeight > 0 && requestedWeight > availableWeight) {
        hasIssue = true;
        issueMessage += `\n  ⚖️ Weight: Requested ${requestedWeight.toFixed(2)}g, Available: ${availableWeight.toFixed(2)}g (Shortage: ${(requestedWeight - availableWeight).toFixed(2)}g)`;
      }

      if (hasIssue) {
        grossStockIssues.push({
          message: issueMessage
        });
      }
    }

    return grossStockIssues;
  }, [items, grossStock]);

  const handleSubmit = async (values) => {
    setIsLoading(true);

    try {
      if (!navigator.onLine) {
        setCustomAlert({
          open: true,
          title: "Network Error",
          message: "No internet connection. Please check your network and try again. Transaction has not been saved.",
          severity: "error",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
        setIsLoading(false);
        return;
      }

      if (!isSecretMode && !showGST) {
        alert("Please add GST to proceed with the transaction.");
        setIsLoading(false);
        return;
      }

      const grossStockIssues = await validateGrossStockAvailability();

      if (grossStockIssues.length > 0) {
        const issueMessages = grossStockIssues.map(issue => issue.message).join('\n\n');

        setCustomAlert({
          open: true,
          title: "⚠️ Gross Stock Insufficient",
          message: `The following items have insufficient GROSS STOCK:\n\n${issueMessages}\n\nPlease adjust quantities or add more stock to Gross Stock before saving this transaction.`,
          severity: "warning",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
        setIsLoading(false);
        return;
      }

      const weightAdjustmentsList = [];
      for (const item of items) {
        if (item.weightAdjusted && item.itemObject) {
          weightAdjustmentsList.push({
            originalItem: item.itemObject,
            adjustedWeight: item.weight,
            adjustmentData: item.weightAdjustmentData,
          });
        }
      }

      let customerId = selectedCustomer?.id;

      if (isNewCustomer && !selectedCustomer) {
        try {
          const formatDate = (date) => {
            if (!date) return "";
            try {
              return format(date, "dd/MM/yyyy");
            } catch {
              return "";
            }
          };

          const newCustomer = {
            name: values.customerName,
            contact: values.newCustomerContact,
            address: values.newCustomerAddress,
            dob: formatDate(values.newCustomerDob) || "",
            anniversary: formatDate(values.newCustomerAnniversary) || "",
            dateAdded: new Date().toISOString().split("T")[0],
          };

          const result = await addCustomer(newCustomer);
          customerId = result?.data?.id;
          setSelectedCustomer({ id: customerId, ...newCustomer });
        } catch (error) {
          console.error("Failed to add new customer:", error);
          setCustomAlert({
            open: true,
            title: "Customer Creation Failed",
            message: "Failed to add new customer. Please try again. Error: " + error.message,
            severity: "error",
            onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
          });
          setIsLoading(false);
          return;
        }
      }

      const billNumberUsed = currentBillNo || 1;
      setUsedBillNo(billNumberUsed);

      let d = new Date(values.transactionDate);
      let month = "" + (d.getMonth() + 1);
      let year = d.getFullYear();
      let date = "" + d.getDate();
      let itemToUpdateId = [];

      const itemsToSave = transactionType === "detailed"
        ? items.map((item, index) => {
          const { id, itemObject, ...itemData } = item;
          let formattedName = itemData.name;
          let itemJson = itemObject || {};

          if (itemJson?.id) {
            itemToUpdateId.push(itemJson.id);
          }

          return {
            itemNumber: index + 1,
            ...itemData,
            name: formattedName,
            metal: itemData.metal || itemJson?.metal || "",
            price: Math.round(parseFloat(itemData.price) || 0),
            serialNo: itemData.serialNo || "",
            melting: itemData.melting || "",
            labour: Math.round(parseFloat(itemData.makingCharges) || 0),
          };
        })
        : [];

      const initialPaymentHistory = [];
      const totalPaymentAmount = Math.round(parseFloat(values.paymentDone) || 0);

      // Determine primary fine entry (first valid one) for payment history
      const validFineEntries = finePayments
        .filter(p => parseFloat(p.weight) > 0)
        .map(p => ({
          metal: p.metal || 'silver',
          weight: parseFloat(p.weight) || 0,
          purity: parseFloat(p.purity) || 0,
          fineWeight: (parseFloat(p.weight) || 0) * (parseFloat(p.purity) || 0) / 100,
        }));

      const primaryFine = validFineEntries.length > 0 ? validFineEntries[0] : null;

      // Create initial payment entry if cash payment OR fine payment exists
      if (totalPaymentAmount > 0 || validFineEntries.length > 0) {
        const initialPaymentEntry = {
          id: `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          date: `'${date}/${month}/${year}`,
          amount: totalPaymentAmount,
          paymentModes: {
            cash: Math.round(parseFloat(values.cashPayment) || 0),
            online: Math.round(parseFloat(values.onlinePayment) || 0),
            cheque: Math.round(parseFloat(values.chequePayment) || 0),
            other: Math.round(parseFloat(values.otherPayment) || 0),
            discount: Math.round(parseFloat(values.discount) || 0),
            advanceBreakup: advancePayments,
            // ✅ FINE PAYMENT — SAME SHAPE AS CUSTOMER LEDGER
            fine: primaryFine,
            // Multi-entry support (Customer Ledger already handles this)
            fineEntries: validFineEntries,
          },
          note: `Initial payment on ${date}/${month}/${year}`,
          recordedBy: values.handledBy || "System"
        };

        initialPaymentHistory.push(initialPaymentEntry);
      }

      const transactionData = {
        customer: customerId,
        customerName: values.customerName,
        date: `'${date}/${month}/${year}`,
        transactionDetails: {
          type: transactionType,
          billNo: billNumberUsed,
          gstApplicable: showGST,
          gstMode: showGST ? gstMode : null,
          gstType: showGST ? gstType : 'cgst_sgst',
          description: transactionDescription,
          originalBillAmount: Math.round(parseFloat(calculations.grandTotal)).toFixed(2),
          balanceDue: Math.round(parseFloat(pendingAmount)).toFixed(2),
          isFullyPaid: pendingAmount <= 0,
          paymentStatus: pendingAmount <= 0 ? "paid" : (totalPaymentAmount > 0 ? "partial" : "unpaid"),
          lastPaymentDate: totalPaymentAmount > 0 ? `'${date}/${month}/${year}` : null,
          lastPaymentAmount: totalPaymentAmount > 0 ? totalPaymentAmount : 0,
          globalMetalRate: parseFloat(values.globalMetalRate) || 0,
          finePayment: {
            enabled: enableFinePayment,
            requiredFine: totalFineRequired,
            paidFine: totalFinePaid,
            totalFinePaid: totalFinePaid,
            balanceFine: balanceFine,
            entries: finePayments.map(p => ({
              metal: p.metal,
              weight: parseFloat(p.weight) || 0,
              purity: parseFloat(p.purity) || 0,
              fineWeight: (parseFloat(p.weight) || 0) * (parseFloat(p.purity) || 0) / 100,
            })),
          },
        },
        calculatedTotals: {
          subtotal: Math.round(parseFloat(calculations.subtotal)).toFixed(2),
          totalItems: items.length.toString(),
        },
        paymentDetails: {
          cash: Math.round(parseFloat(parseFloat(values.cashPayment).toFixed(2)) || 0),
          online: Math.round(parseFloat(parseFloat(values.onlinePayment).toFixed(2)) || 0),
          cheque: Math.round(parseFloat(parseFloat(values.chequePayment).toFixed(2)) || 0),
          other: Math.round(parseFloat(parseFloat(values.otherPayment).toFixed(2)) || 0),
          advancePayment: {
            enabled: enableAdvancePayment,
            total: Math.round(parseFloat(advanceTotal).toFixed(2)) || 0,
            breakup: advancePayments,
          },
          totalPaymentDone: totalPaymentAmount,
          discount: Math.round(parseFloat(parseFloat(values.discount).toFixed(2)) || 0),
          paymentHistory: initialPaymentHistory,
          finePayment: {
            totalFineRequired: totalFineRequired,
            totalFinePaid: totalFinePaid,
            balanceFine: balanceFine,
            entries: validFineEntries,
          },
        },
        metaDetails: {
          handledBy: values.handledBy,
          gstNumber: values.gstNumber,
          panNumber: values.panNumber,
        },
        items: itemsToSave,
        totalAmount: transactionType === "total" ? Math.round(parseFloat(values.totalAmount).toFixed(2) || 0) : 0,
        summary: {
          grandTotal: Math.round(parseFloat(calculations.grandTotal).toFixed(2)) || 0,
          paymentDone: totalPaymentAmount,
          pendingAmount: Math.round(parseFloat(pendingAmount).toFixed(2)) || 0,
          cgst: Math.round(parseFloat(calculations.cgst).toFixed(2)) || 0,
          sgst: Math.round(parseFloat(calculations.sgst).toFixed(2)) || 0,
          igst: Math.round(parseFloat(calculations.igst).toFixed(2)) || 0,
          fineRequired: totalFineRequired,
          finePaid: totalFinePaid,
          fineBalance: balanceFine > 0 ? balanceFine : 0,
        },
      };

      const savePromise = addCustomerTransactionAndUpdateStock(
        transactionData,
        itemToUpdateId,
      );

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), 30000);
      });

      const saveResult = await Promise.race([savePromise, timeoutPromise]);

      if (saveResult?.success) {
        if (selectedCustomer?.id && saveResult?.data?.transaction?.id) {
          try {
            const billId = saveResult.data.transaction.id;
            const billNoUsed = transactionData.transactionDetails.billNo;

            const existingAdvanceUsage = {};
            const manualAdvancePayments = {};

            Object.entries(advancePayments).forEach(([mode, amount]) => {
              const numAmount = parseFloat(amount) || 0;
              if (numAmount > 0) {
                const availableBalance = customerAdvanceBalance?.byMode?.[mode] || 0;

                if (availableBalance > 0) {
                  const deductAmount = Math.min(numAmount, availableBalance);
                  if (deductAmount > 0) {
                    existingAdvanceUsage[mode] = deductAmount;
                  }
                  const excessAmount = numAmount - deductAmount;
                  if (excessAmount > 0) {
                    manualAdvancePayments[mode] = excessAmount;
                  }
                } else {
                  manualAdvancePayments[mode] = numAmount;
                }
              }
            });

            const totalExistingAdvanceUsed = Object.values(existingAdvanceUsage).reduce((sum, val) => sum + val, 0);

            if (totalExistingAdvanceUsed > 0) {
              const advanceResult = await applyAdvanceToBill(
                selectedCustomer.id,
                billId,
                billNoUsed,
                existingAdvanceUsage,
                totalExistingAdvanceUsed
              );

              if (advanceResult.success) {
                console.log("Advance payment applied successfully");
                setSnackbar({
                  open: true,
                  message: `Advance payment of ₹${totalExistingAdvanceUsed.toFixed(2)} applied successfully!`,
                  severity: 'success'
                });
              } else {
                console.warn("Failed to apply advance:", advanceResult.error);
                setSnackbar({
                  open: true,
                  message: "Transaction saved but advance payment could not be applied. Please check manually.",
                  severity: 'warning'
                });
              }
            }

            const totalManualAdvance = Object.values(manualAdvancePayments).reduce((sum, val) => sum + val, 0);
            if (totalManualAdvance > 0) {
              const manualModes = Object.keys(manualAdvancePayments).join(', ');
              setSnackbar({
                open: true,
                message: `Manual advance payments of ₹${totalManualAdvance.toFixed(2)} (${manualModes}) recorded as regular payments.`,
                severity: 'info'
              });
            }

          } catch (advanceError) {
            console.error("Error recording advance usage:", advanceError);
          }
        }
      }

      setIsTransactionSaved(true);
      setCustomAlert({
        open: true,
        title: "Transaction Successful",
        message: `Transaction added successfully! Bill No: ${currentBillNo}`,
        severity: "success",
        onConfirm: () => {
          setCustomAlert({ ...customAlert, open: false });
        },
      });

    } catch (error) {
      console.error("Failed to add transaction:", error);

      if (error.message === "Request timeout") {
        setCustomAlert({
          open: true,
          title: "Request Timeout",
          message: "The request timed out. Please check your network connection and try again. Transaction has not been saved.",
          severity: "error",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
      } else if (error.message === "Failed to fetch" || error.name === "NetworkError") {
        setCustomAlert({
          open: true,
          title: "Network Error",
          message: "Network connection lost. Please check your internet connection and try again. Transaction has not been saved.",
          severity: "error",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        setCustomAlert({
          open: true,
          title: "Authentication Error",
          message: "Your session may have expired. Please refresh the page and try again.",
          severity: "error",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
      } else {
        setCustomAlert({
          open: true,
          title: "Transaction Failed",
          message: `Failed to add transaction: ${error.message || "Unknown error"}. Please try again.`,
          severity: "error",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Desktop printing function
  const handleDesktopPrint = useCallback((billHTML, styles) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the bill');
      return;
    }

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print Bill - ${showGST ? 'GST Invoice' : 'Cash Memo'}</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          ${styles}
          
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            table {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            tr {
              page-break-inside: avoid !important;
              page-break-after: auto !important;
              break-inside: avoid !important;
            }
            
            thead {
              display: table-header-group !important;
            }
            
            tfoot {
              display: table-footer-group !important;
            }
          }
          
          .watermark {
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) rotate(-45deg) !important;
            opacity: 0.1 !important;
            z-index: -1 !important;
            pointer-events: none !important;
          }
          
          .watermark img {
            width: 400px !important;
            height: 400px !important;
            object-fit: contain !important;
          }
        </style>
      </head>
      <body>
        ${billHTML}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                setTimeout(() => window.close(), 100);
              };
            }, 500);
          };
        </script>
      </body>
    </html>
  `);

    printWindow.document.close();
  }, [showGST]);

  // Mobile-optimized printing function
  const handleMobilePrint = useCallback((billHTML, styles) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;

    iframeDoc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print Bill - ${showGST ? 'GST Invoice' : 'Cash Memo'}</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes">
        <style>
          ${styles}
          
          @media print {
            body {
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            #billContent, #billContentWithoutGst {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              border: 2px solid #2c3e50 !important;
            }
            
            table {
              width: 100% !important;
              table-layout: fixed !important;
            }
            
            td, th {
              word-wrap: break-word !important;
              white-space: normal !important;
            }
          }
          
          .print-loading {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: #2c3e50;
            color: white;
            padding: 15px 30px;
            border-radius: 8px;
            font-family: Arial, sans-serif;
            z-index: 9999;
            text-align: center;
            box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          }
          
          .print-loading:after {
            content: '';
            display: inline-block;
            width: 20px;
            height: 20px;
            margin-left: 10px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #d4af37;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            vertical-align: middle;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      </head>
      <body>
        <div class="print-loading">Preparing bill for printing...</div>
        ${billHTML}
        <script>
          window.onload = function() {
            const loadingEl = document.querySelector('.print-loading');
            if (loadingEl) {
              loadingEl.style.display = 'none';
            }
            
            setTimeout(function() {
              try {
                window.print();
              } catch(e) {
                console.error('Print error:', e);
                alert('Unable to print. Please try again or use a different browser.');
              }
              
              window.onafterprint = function() {
                setTimeout(function() {
                  window.parent.document.body.removeChild(window.frameElement);
                }, 100);
              };
              
              setTimeout(function() {
                if (window.parent && window.frameElement) {
                  window.parent.document.body.removeChild(window.frameElement);
                }
              }, 30000);
            }, 1000);
          };
        </script>
      </body>
    </html>
  `);

    iframeDoc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
    }, 500);
  }, [showGST]);

  const handlePrintBill = useCallback(() => {
    if (!formik.values.customerName.trim()) {
      alert("Please select or enter a customer name");
      return;
    }

    if (!formik.values.handledBy.trim()) {
      alert("Please enter seller name");
      return;
    }

    if (transactionType === "total" && (!formik.values.totalAmount || parseFloat(formik.values.totalAmount) <= 0)) {
      alert("Please enter a valid total amount");
      return;
    }

    let billNo = currentBillNo || 1;
    if (isTransactionSaved && usedBillNo) {
      billNo = usedBillNo;
    } else {
      billNo = currentBillNo || 1;
    }

    let d = new Date(formik.values.transactionDate);
    let month = "" + (d.getMonth() + 1);
    if (month.length === 1) month = "0" + month;
    let year = d.getFullYear();
    let date = "" + d.getDate();
    if (date.length === 1) date = "0" + date;

    const itemsForBill = transactionType === "detailed"
      ? items.map((item, index) => {
        const { id, itemObject, ...itemData } = item;
        let displayName = itemData.name;
        if (itemObject) {
          const itemObj = parseItemObject(itemObject);
          displayName = `${itemObj.name || itemData.name} ${itemObj.huid ? `- ${itemObj.huid}` : ""}`;
        }
        return {
          ...itemData,
          name: displayName,
          itemObject: itemObject || null,
          price: Math.round(parseFloat(itemData.price) || 0),
          serialNo: itemData.serialNo || "",
          melting: itemData.melting || "",
        };
      })
      : [];

    const advanceTotalForBill = enableAdvancePayment
      ? Object.values(advancePayments).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
      : 0;

    const transactionDataForBill = {
      transactionDetails: {
        type: transactionType,
        billNo,
        gstApplicable: showGST,
        gstMode: showGST ? gstMode : null,
        gstType: showGST ? gstType : 'cgst_sgst',
        globalMetalRate: parseFloat(formik.values.globalMetalRate) || 0,
      },
      date: `'${date}/${month}/${year}`,
      description: transactionDescription,
      calculatedTotals: {
        subtotal: Math.round(calculations.subtotal),
      },
      paymentDetails: {
        cash: Math.round(parseFloat(formik.values.cashPayment) || 0),
        online: Math.round(parseFloat(formik.values.onlinePayment) || 0),
        cheque: Math.round(parseFloat(formik.values.chequePayment) || 0),
        other: Math.round(parseFloat(formik.values.otherPayment) || 0),
        advancePayment: {
          enabled: enableAdvancePayment,
          total: Math.round(advanceTotalForBill),
          breakup: advancePayments,
        },
        discount: Math.round(parseFloat(formik.values.discount) || 0),
      },
      metaDetails: {
        handledBy: formik.values.handledBy,
        gstNumber: formik.values.gstNumber || "",
        panNumber: formik.values.panNumber || "",
      },
      totalAmount: transactionType === "total" ? Math.round(parseFloat(formik.values.totalAmount) || 0) : 0,
      summary: {
        grandTotal: Math.round(calculations.grandTotal),
        paymentDone: Math.round(parseFloat(formik.values.paymentDone) || 0),
        pendingAmount: pendingAmount > 0 ? Math.round(pendingAmount) : 0,
        cgst: Math.round(calculations.cgst),
        sgst: Math.round(calculations.sgst),
        igst: Math.round(calculations.igst),
      },
    };

    const validFineEntries = finePayments
      .filter(p => parseFloat(p.weight) > 0)
      .map(p => ({
        metal: p.metal || 'silver',
        weight: parseFloat(p.weight) || 0,
        purity: parseFloat(p.purity) || 0,
        fineWeight: (parseFloat(p.weight) || 0) * (parseFloat(p.purity) || 0) / 100,
      }));

    const fineData = {
      required: totalFineRequired,
      paid: totalFinePaid,
      balance: balanceFine,
      entries: validFineEntries,
      hasFine: totalFineRequired > 0 || totalFinePaid > 0,
    };

    const billData = {
      type: showGST ? 'gst' : 'non-gst',
      data: {
        ...transactionDataForBill,
        fineData: fineData,
      },
      items: itemsForBill,
      payments: transactionDataForBill.paymentDetails,
      customer: selectedCustomer || {
        name: formik.values.customerName,
        contact: formik.values.newCustomerContact || "",
        address: formik.values.newCustomerAddress || ""
      },
      advanceData: advancePayments
    };

    const billHTML = generateBillHTML(billData);
    const styles = getBillStyles();

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isMobile) {
      handleMobilePrint(billHTML, styles);
    } else {
      handleDesktopPrint(billHTML, styles);
    }
  }, [formik.values, selectedCustomer, showGST, gstMode, gstType, transactionType, items, enableAdvancePayment, advancePayments, calculations, pendingAmount, handleDesktopPrint, handleMobilePrint, currentBillNo, isTransactionSaved, usedBillNo, transactionDescription, finePayments, totalFineRequired, totalFinePaid, balanceFine]);

  const resetForm = useCallback(() => {
    formik.resetForm();
    setIsTransactionSaved(false);
    setUsedBillNo(null);
    setSelectedCustomer(null);
    setCustomerQuery("");
    setIsNewCustomer(false);
    setShowNewCustomerFields(false);
    setItems([]);
    setTransactionType("detailed");
    setShowGST(false);
    setGstMode("add");
    setGstType("cgst_sgst");
    setEnableAdvancePayment(false);
    setPaymentModes({
      cash: false,
      online: false,
      oldGold: false,
      oldSilver: false,
    });
    setAdvancePayments({});
    setManualItemName("");
    setManualSerial("");
    setTransactionDescription("");
    fetchLastBillNumbers();
    setFinePayments([]);
    setEnableFinePayment(false);
    setFinePaymentModes({ silver: false, gold: false });
    hasShownAdvanceDialogRef.current = false;
  }, [formik, fetchLastBillNumbers]);

  // UI rendering functions - modified for new columns
  // In renderItemRows, update the weight and price display
  const renderItemRows = useMemo(
    () =>
      items.map((item) => {
        const displayWeight = item.weight || 0;
        const displayMetalRate = item.metalRate || '';
        const displayMakingCharges = item.makingCharges || 0;
        const itemPrice = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
        const hasStockItem = !!item.itemObject;
        const isWeightAdjusted = item.weightAdjusted || item.adjustmentApplied;
        const needsAdjustment = pendingWeightAdjustments?.[item?.id]?.needsAdjustment;
        const isPerPieceItem = item.isPerPieceItem || (item.weight === 0 && item.price > 0);

        // Display melting as-is (no conversion to K)
        const displayMelting = item?.melting || item?.ct || "";

        return (
          <TableRow
            key={item.id}
            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
          >
            <TableCell>
              <TextField
                value={item.name}
                onChange={(e) =>
                  updateItemField(item.id, "name", e.target.value)
                }
                fullWidth
                variant="standard"
                sx={{
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: `2px solid ${alpha(
                        isDark ? '#60a5fa' : '#3b82f6',
                        isDark ? 0.5 : 0.7
                      )}`,
                    },
                  }
                }}
              />
            </TableCell>
            <TableCell>
              <TextField
                value={item.serialNo || ''}
                onChange={(e) =>
                  updateItemField(item.id, "serialNo", e.target.value)
                }
                fullWidth
                variant="standard"
                sx={{
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: `2px solid ${alpha(
                        isDark ? '#60a5fa' : '#3b82f6',
                        isDark ? 0.5 : 0.7
                      )}`,
                    },
                  }
                }}
              />
            </TableCell>
            <TableCell>
              <TextField
                value={item.metal || ''}
                onChange={(e) =>
                  updateItemField(item.id, "metal", e.target.value)
                }
                fullWidth
                variant="standard"
                sx={{
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: `2px solid ${alpha(
                        isDark ? '#60a5fa' : '#3b82f6',
                        isDark ? 0.5 : 0.7
                      )}`,
                    },
                  }
                }}
              />
            </TableCell>
            <TableCell>
              <TextField
                value={displayMelting}
                onChange={(e) => {
                  updateItemField(item.id, "melting", e.target.value);
                }}
                fullWidth
                size="small"
                variant="standard"
                sx={{
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: `2px solid ${alpha(
                        isDark ? '#60a5fa' : '#3b82f6',
                        isDark ? 0.5 : 0.7
                      )}`,
                    },
                  }
                }}
              />
            </TableCell>
            <TableCell>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TextField
                  type="text"
                  value={displayWeight}
                  onClick={() => hasStockItem && openWeightEditDialog(item)}
                  fullWidth
                  size="small"
                  variant="standard"
                  inputProps={{
                    step: "0.01",
                    readOnly: isPerPieceItem || hasStockItem,
                    style: { cursor: hasStockItem ? "pointer" : isPerPieceItem ? "default" : "text" },
                  }}
                  sx={{
                    "& .MuiInput-root": {
                      "&::before": {
                        borderBottom: `1px solid ${alpha(
                          isDark ? theme.palette.divider : "#e2e8f0",
                          isDark ? 0.2 : 0.3,
                        )}`,
                      },
                    },
                    "& .MuiInput-input": {
                      color: isPerPieceItem ? theme.palette.info.main :
                        (item.weightAdjusted && !needsAdjustment
                          ? theme.palette.success.main
                          : needsAdjustment
                            ? theme.palette.warning.main
                            : "inherit"),
                      fontWeight: isPerPieceItem ? 600 : (needsAdjustment ? 600 : "normal"),
                      cursor: hasStockItem ? "pointer" : (isPerPieceItem ? "default" : "text"),
                    },
                    "& .MuiInput-input:hover": {
                      cursor: hasStockItem ? "pointer" : (isPerPieceItem ? "default" : "text"),
                    },
                  }}
                />
                {isPerPieceItem && (
                  <Chip
                    label="Per Piece"
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{
                      position: "absolute",
                      right: -60,
                      top: -8,
                      height: 20,
                      fontSize: "0.6rem",
                      fontWeight: 600,
                    }}
                  />
                )}
                {hasStockItem && (
                  <IconButton
                    size="small"
                    onClick={() => openWeightEditDialog(item)}
                    sx={{ ml: 1, p: 0.5 }}
                    title="Click to edit weight"
                  >
                    <EditIcon fontSize="small" sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {item.weightAdjusted && !needsAdjustment && !isPerPieceItem && (
                  <Chip
                    label="Adjusted"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{
                      position: "absolute",
                      right: -60,
                      top: -8,
                      height: 20,
                      fontSize: "0.6rem",
                      fontWeight: 600,
                    }}
                  />
                )}
                {needsAdjustment && !isPerPieceItem && (
                  <Chip
                    label="Pending"
                    size="small"
                    color="warning"
                    sx={{
                      position: "absolute",
                      right: -60,
                      top: -8,
                      height: 20,
                      fontSize: "0.6rem",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            </TableCell>
            <TableCell>
              <TextField
                type="text"
                value={isPerPieceItem ? "N/A" : displayMetalRate}
                onChange={(e) => {
                  if (!isPerPieceItem) {
                    updateItemField(item.id, "metalRate", e.target.value);
                  }
                }}
                fullWidth
                size="small"
                variant="standard"
                inputProps={{ step: "0.01", readOnly: isPerPieceItem }}
                sx={{
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                  },
                  '& .MuiInput-input': {
                    color: isPerPieceItem ? theme.palette.text.disabled : 'inherit',
                  }
                }}
              />
            </TableCell>
            <TableCell>
              <TextField
                type="text"
                value={isPerPieceItem ? "N/A" : displayMakingCharges}
                onChange={(e) => {
                  if (!isPerPieceItem) {
                    const value = e.target.value;
                    if (value === '' || /^(\d*\.?\d*)$/.test(value)) {
                      updateItemField(item.id, "makingCharges", value);
                    }
                  }
                }}
                fullWidth
                size="small"
                variant="standard"
                inputProps={{ step: "0.01", readOnly: isPerPieceItem }}
                sx={{
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                  },
                  '& .MuiInput-input': {
                    color: isPerPieceItem ? theme.palette.text.disabled : 'inherit',
                  }
                }}
              />
            </TableCell>
            <TableCell>
              <TextField
                type="text"
                value={itemPrice}
                onChange={(e) => updateItemField(item.id, "price", e.target.value)}
                fullWidth
                size="small"
                variant="standard"
                inputProps={{ step: "1" }}
                sx={{
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: `2px solid ${alpha(
                        isDark ? '#60a5fa' : '#3b82f6',
                        isDark ? 0.5 : 0.7
                      )}`,
                    },
                  },
                  '& .MuiInput-input': {
                    fontWeight: 600,
                    color: isPerPieceItem ? theme.palette.info.main : (isDark ? '#e2e8f0' : '#1e293b'),
                  }
                }}
              />
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={1}>
                {hasStockItem && (
                  <IconButton
                    onClick={() => openWeightEditDialog(item)}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: needsAdjustment
                        ? alpha(theme.palette.warning.main, isDark ? 0.3 : 0.2)
                        : alpha(theme.palette.info.main, isDark ? 0.2 : 0.1),
                      color: needsAdjustment
                        ? theme.palette.warning.main
                        : theme.palette.info.main,
                      border: `1px solid ${alpha(
                        needsAdjustment
                          ? theme.palette.warning.main
                          : theme.palette.info.main,
                        isDark ? 0.3 : 0.2,
                      )}`,
                      "&:hover": {
                        backgroundColor: alpha(
                          needsAdjustment
                            ? theme.palette.warning.main
                            : theme.palette.info.main,
                          isDark ? 0.4 : 0.25,
                        ),
                      },
                    }}
                    title={needsAdjustment ? "Review weight adjustment" : "Edit weight"}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {isWeightAdjusted && !needsAdjustment && (
                  <Chip
                    label="Adjusted"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 24, fontSize: "0.7rem" }}
                  />
                )}
                <IconButton
                  onClick={() => removeItemRow(item.id)}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: alpha(
                      theme.palette.error.main,
                      isDark ? 0.2 : 0.1,
                    ),
                    color: theme.palette.error.main,
                    border: `1px solid ${alpha(theme.palette.error.main, isDark ? 0.3 : 0.2)}`,
                    "&:hover": {
                      backgroundColor: alpha(
                        theme.palette.error.main,
                        isDark ? 0.3 : 0.15,
                      ),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </TableCell>
          </TableRow>
        );
      }),
    [
      items,
      updateItemField,
      removeItemRow,
      isDark,
      theme,
      pendingWeightAdjustments,
      openWeightEditDialog
    ],
  );

  const renderCustomerSuggestions = useMemo(
    () =>
      customerSuggestions.map((customer) => (
        <Box
          key={customer.id}
          sx={{
            p: 1.5,
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : '#ffffff',
              isDark ? 0.3 : 0.6
            ),
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(
              isDark ? theme.palette.divider : '#e2e8f0',
              isDark ? 0.2 : 0.3
            )}`,
            borderRadius: 1,
            mb: 1,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : '#ffffff',
                isDark ? 0.5 : 0.8
              ),
              borderColor: alpha(
                isDark ? '#60a5fa' : '#3b82f6',
                isDark ? 0.4 : 0.5
              ),
              transform: 'translateX(2px)',
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          onClick={() => handleCustomerSelect(customer)}
        >
          {customer.isNew ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: isDark ? '#60a5fa' : '#3b82f6',
              }}
            >
              <AddIcon sx={{ mr: 1, fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Add "{customerQuery}" as new customer
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Person
                  sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#64748b" }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {customer.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? "#94a3b8" : "#94a3b8" }}
                >
                  ({customer.contact})
                </Typography>
              </Box>
              {(customer.gstNo || customer.panNo) && (
                <Box sx={{ display: "flex", gap: 1, ml: 3 }}>
                  {customer.gstNo && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#60a5fa" : "#3b82f6",
                        fontSize: "0.65rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <AccountBalanceIcon sx={{ fontSize: 12 }} />
                      GST: {customer.gstNo}
                    </Typography>
                  )}
                  {customer.panNo && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#94a3b8" : "#64748b",
                        fontSize: "0.65rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <InfoIcon sx={{ fontSize: 12 }} />
                      PAN: {customer.panNo}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )),
    [customerSuggestions, customerQuery, handleCustomerSelect, isDark, theme]
  );

  // Show skeleton during initial loading OR actual data loading
  if (initialLoading || isLoading) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ minHeight: "100vh", py: 3 }}>
          <Box sx={{ maxWidth: "1400px", mx: "auto", px: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={4}>
              <Skeleton variant="text" width={300} height={48} />
            </Stack>

            <Grid container spacing={3} alignItems="flex-start">
              <Grid item size={{ xs: 12, md: 12 }}>
                <CustomerInfoSkeleton isDark={isDark} />
              </Grid>

              <Grid item size={{ xs: 12, md: 12 }}>
                <Stack spacing={3}>
                  <ItemsSectionSkeleton isDark={isDark} />
                  <GstSectionSkeleton isDark={isDark} />
                  <PaymentSectionSkeleton isDark={isDark} />
                </Stack>
              </Grid>

              <Grid item size={{ xs: 12, md: 12 }}>
                <Stack spacing={3} sx={{ position: "sticky", top: 24 }}>
                  <SummarySkeleton isDark={isDark} />
                  <ActionButtonsSkeleton isDark={isDark} />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: "100vh",
          py: 3,
        }}
      >
        <ManualItemDialog
          open={manualItemDialogOpen}
          onClose={() => setManualItemDialogOpen(false)}
          onSubmit={handleAddManualItem}
          manualItemDetails={manualItemDetails}
          setManualItemDetails={setManualItemDetails}
          globalMetalRate={formik.values.globalMetalRate}
        />

        <Box
          sx={{
            maxWidth: "1400px",
            mx: "auto",
            px: 2,
          }}
        >
          {/* HEADER */}
          <Stack direction="row" alignItems="center" spacing={2} mb={4}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                background: isDark
                  ? 'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)'
                  : 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
              New Customer Transaction
            </Typography>
          </Stack>

          {/* MAIN 3-COLUMN GRID */}
          <Grid container spacing={3} alignItems="flex-start">
            {/* LEFT COLUMN — CUSTOMER INFORMATION PANEL */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backdropFilter: 'blur(20px)',
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.6
                  ),
                  border: `1px solid ${alpha(
                    isDark ? theme.palette.divider : '#e2e8f0',
                    isDark ? 0.2 : 0.3
                  )}`,
                  boxShadow: theme.shadows[4],
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isDark
                        ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                        : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1rem',
                    }}
                  >
                    1
                  </Box>

                  <Typography variant="h6" sx={{
                    fontWeight: 700,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Person fontSize="small" />
                    Customer Information
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  <TextField
                    label="Search Customer"
                    placeholder="Type name or contact..."
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.2 : 0.7
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                        },
                        '&.Mui-focused': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: isDark ? '#cbd5e1' : '#64748b',
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {customerSuggestions.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        backdropFilter: 'blur(20px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.3 : 0.85
                        ),
                        border: `1px solid ${alpha(
                          isDark ? theme.palette.divider : '#e2e8f0',
                          isDark ? 0.2 : 0.3
                        )}`,
                        boxShadow: theme.shadows[4],
                        overflow: 'hidden',
                        maxHeight: customerSuggestions.length > 5 ? '260px' : 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          borderBottom: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.1 : 0.2
                          )}`,
                          backgroundColor: alpha(
                            isDark ? '#1e293b' : '#f8fafc',
                            isDark ? 0.4 : 0.5
                          ),
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? '#cbd5e1' : '#475569',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <SearchIcon fontSize="inherit" />
                          {customerSuggestions.length} suggestion{customerSuggestions.length !== 1 ? 's' : ''} found
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          overflowY: 'auto',
                          maxHeight: '210px',
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: alpha(
                              isDark ? '#1e293b' : '#f1f5f9',
                              isDark ? 0.3 : 0.5
                            ),
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.4 : 0.5
                            ),
                            borderRadius: '3px',
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? '#3b82f6' : '#2563eb',
                                isDark ? 0.6 : 0.7
                              ),
                            },
                          },
                        }}
                      >
                        {renderCustomerSuggestions}
                      </Box>

                      {customerSuggestions.length > 5 && (
                        <Box
                          sx={{
                            p: 1,
                            borderTop: `1px solid ${alpha(
                              isDark ? theme.palette.divider : '#e2e8f0',
                              isDark ? 0.1 : 0.2
                            )}`,
                            textAlign: 'center',
                            backgroundColor: alpha(
                              isDark ? '#1e293b' : '#f8fafc',
                              isDark ? 0.4 : 0.5
                            ),
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? '#94a3b8' : '#64748b',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                            }}
                          >
                            <span>Scroll to see {customerSuggestions.length - 5} more</span>
                            <span style={{ transform: 'rotate(90deg)' }}>›</span>
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  )}

                  {isNewCustomer && !selectedCustomer && customerQuery.trim() && (
                    <Alert
                      severity="info"
                      icon={<InfoIcon fontSize="small" />}
                      sx={{
                        borderRadius: 2,
                        alignItems: 'center',
                        py: 1,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.info.main : theme.palette.info.main,
                          isDark ? 0.2 : 0.1
                        ),
                        border: `1px solid ${alpha(
                          isDark ? theme.palette.info.main : theme.palette.info.main,
                          isDark ? 0.3 : 0.2
                        )}`,
                        '& .MuiAlert-message': {
                          flex: 1,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                          Customer not found. Add "{customerQuery}" as new customer.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            formik.setFieldValue("customerName", customerQuery);
                            formik.setFieldValue("isNewCustomer", true);
                            setShowNewCustomerFields(true);
                            setIsNewCustomer(true);
                            setSelectedCustomer(null);
                            setCustomerSuggestions([]);
                          }}
                          sx={{
                            ml: 2,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            py: 0.25,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.3 : 0.8
                            ),
                            borderColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.4 : 0.5
                            ),
                            color: isDark ? '#60a5fa' : '#3b82f6',
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.5 : 0.9
                              ),
                              borderColor: alpha(
                                isDark ? '#60a5fa' : '#3b82f6',
                                isDark ? 0.6 : 0.7
                              ),
                            }
                          }}
                        >
                          Add Now
                        </Button>
                      </Box>
                    </Alert>
                  )}
                </Stack>

                {selectedCustomer && (
                  <Paper
                    sx={{
                      mt: 3,
                      p: 2.5,
                      borderRadius: 2,
                      backdropFilter: 'blur(20px)',
                      background: isDark
                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%)',
                      border: `1px solid ${alpha(
                        isDark ? '#10b981' : '#059669',
                        isDark ? 0.25 : 0.2
                      )}`,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '60px',
                        height: '60px',
                        background: isDark
                          ? 'linear-gradient(135deg, transparent 50%, rgba(16, 185, 129, 0.1) 50%)'
                          : 'linear-gradient(135deg, transparent 50%, rgba(5, 150, 105, 0.1) 50%)',
                      }}
                    />

                    <Stack direction="row" spacing={2} alignItems="center" position="relative" zIndex={1}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isDark
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                        }}
                      >
                        <Person fontSize="small" />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b', mb: 0.5 }}>
                          {selectedCustomer.name}
                          {selectedCustomer.contact && (
                            <Typography component="span" sx={{ color: isDark ? '#94a3b8' : '#64748b', ml: 1, fontWeight: 400 }}>
                              ({selectedCustomer.contact})
                            </Typography>
                          )}
                        </Typography>

                        {selectedCustomer.address && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? '#cbd5e1' : '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Home fontSize="inherit" />
                            {selectedCustomer.address}
                          </Typography>
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedCustomer(null);
                          setCustomerQuery('');
                          formik.setFieldValue("customerName", '');
                        }}
                        sx={{
                          borderRadius: 1.5,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                          )}`,
                          color: isDark ? '#cbd5e1' : '#64748b',
                          '&:hover': {
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.5 : 0.9
                            ),
                            color: isDark ? '#ef4444' : '#dc2626',
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                )}

                {showNewCustomerFields && (
                  <Card
                    sx={{
                      mt: 3,
                      p: 2.5,
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)',
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : '#ffffff',
                        isDark ? 0.3 : 0.7
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                      boxShadow: 'none',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? '#e2e8f0' : '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Person fontSize="small" />
                      New Customer Details
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Contact Number *"
                          name="newCustomerContact"
                          value={formik.values.newCustomerContact}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.newCustomerContact && Boolean(formik.errors.newCustomerContact)}
                          helperText={formik.touched.newCustomerContact && formik.errors.newCustomerContact}
                          fullWidth
                          size="small"
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.2 : 0.7
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.3 : 0.8
                                ),
                              }
                            },
                            '& .MuiOutlinedInput-input': {
                              color: isDark ? '#e2e8f0' : '#1e293b',
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone fontSize="small" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Address"
                          name="newCustomerAddress"
                          value={formik.values.newCustomerAddress}
                          onChange={formik.handleChange}
                          multiline
                          rows={2}
                          fullWidth
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.2 : 0.7
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.3 : 0.8
                                ),
                              }
                            },
                            '& .MuiOutlinedInput-input': {
                              color: isDark ? '#e2e8f0' : '#1e293b',
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Home fontSize="small" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Date of Birth"
                          value={formik.values.newCustomerDob}
                          format="dd/MM/yyyy"
                          onChange={(date) => formik.setFieldValue("newCustomerDob", date)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? theme.palette.background.paper : '#ffffff',
                                    isDark ? 0.2 : 0.7
                                  ),
                                  '&:hover': {
                                    backgroundColor: alpha(
                                      isDark ? theme.palette.background.paper : '#ffffff',
                                      isDark ? 0.3 : 0.8
                                    ),
                                  }
                                },
                                '& .MuiOutlinedInput-input': {
                                  color: isDark ? '#e2e8f0' : '#1e293b',
                                },
                              }
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Anniversary"
                          value={formik.values.newCustomerAnniversary}
                          format="dd/MM/yyyy"
                          onChange={(date) => formik.setFieldValue("newCustomerAnniversary", date)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? theme.palette.background.paper : '#ffffff',
                                    isDark ? 0.2 : 0.7
                                  ),
                                  '&:hover': {
                                    backgroundColor: alpha(
                                      isDark ? theme.palette.background.paper : '#ffffff',
                                      isDark ? 0.3 : 0.8
                                    ),
                                  }
                                },
                                '& .MuiOutlinedInput-input': {
                                  color: isDark ? '#e2e8f0' : '#1e293b',
                                },
                              }
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                )}

                <Divider sx={{
                  my: 3,
                  borderColor: alpha(
                    isDark ? theme.palette.divider : '#e2e8f0',
                    isDark ? 0.1 : 0.2
                  ),
                }} />

                <Stack spacing={2}>
                  <TextField
                    label="Seller Name *"
                    name="handledBy"
                    value={formik.values.handledBy}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.handledBy && Boolean(formik.errors.handledBy)}
                    helperText={formik.touched.handledBy && formik.errors.handledBy}
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.3 : 0.8
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.4 : 0.85
                          ),
                        },
                        '&.Mui-focused': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.4 : 0.85
                          ),
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: isDark ? '#cbd5e1' : '#64748b',
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                  />

                  <DatePicker
                    label="Transaction Date *"
                    value={formik.values.transactionDate}
                    onChange={(date) => formik.setFieldValue("transactionDate", date)}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.3 : 0.8
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.4 : 0.85
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }
                      },
                    }}
                  />

                  <TextField
                    label="Global Metal Rate (per gram) *"
                    name="globalMetalRate"
                    value={formik.values.globalMetalRate}
                    onChange={(e) => {
                      formik.handleChange(e);
                      const rate = parseFloat(e.target.value) || 0;
                      if (rate > 0 && items.length > 0) {
                        setItems(prev =>
                          prev.map(item => {
                            const weight = parseFloat(item.weight) || 0;
                            const makingCharges = parseFloat(item.makingCharges) || 0;
                            let price = (rate + makingCharges) * weight;

                            if (showGST && gstMode === "adjust") {
                              const adjustedMakingCharges = (price / (1.03 * weight)) - rate;
                              price = (rate + adjustedMakingCharges) * weight;
                              return {
                                ...item,
                                metalRate: rate,
                                makingCharges: parseFloat(adjustedMakingCharges.toFixed(2)),
                                price: Math.round(price),
                              };
                            }

                            return {
                              ...item,
                              metalRate: rate,
                              price: Math.round(price),
                            };
                          })
                        );
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.globalMetalRate && Boolean(formik.errors.globalMetalRate)}
                    helperText={formik.touched.globalMetalRate && formik.errors.globalMetalRate}
                    fullWidth
                    required={showGST}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.3 : 0.8
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.4 : 0.85
                          ),
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="GST Number"
                    name="gstNumber"
                    value={formik.values.gstNumber}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.3 : 0.8
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.4 : 0.85
                          ),
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                  />

                  <TextField
                    label="PAN Number"
                    name="panNumber"
                    value={formik.values.panNumber}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.3 : 0.8
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.4 : 0.85
                          ),
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                  />
                </Stack>
              </Card>
            </Grid>

            {/* CENTER COLUMN — ITEMS • GST • PAYMENT PANEL */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Stack spacing={3}>
                {/* DETAILED ITEM ENTRY SECTION */}
                {transactionType === "detailed" && (
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backdropFilter: 'blur(20px)',
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : '#ffffff',
                        isDark ? 0.3 : 0.6
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 700,
                        color: isDark ? '#e2e8f0' : '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <ShoppingBag fontSize="small" />
                        Purchase Items
                      </Typography>

                      <Stack direction="row" spacing={2}>
                        {isSecretMode && <Button
                          startIcon={<AddIcon />}
                          variant="contained"
                          size="medium"
                          onClick={() => setManualItemDialogOpen(true)}
                          sx={{
                            borderRadius: 2,
                            background: isDark
                              ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                              : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                            '&:hover': {
                              background: isDark
                                ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                                : 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                            }
                          }}
                        >
                          Add from Gross Stock
                        </Button>}
                        <Button
                          variant="contained"
                          startIcon={<QrCode />}
                          onClick={() => setScannerOpen(true)}
                          sx={{
                            borderRadius: 2,
                            background: isDark
                              ? 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
                              : 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.25)',
                            '&:hover': {
                              background: isDark
                                ? 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)'
                                : 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                            }
                          }}
                        >
                          Scan QR
                        </Button>
                      </Stack>

                      <QRScanner
                        open={scannerOpen}
                        onClose={() => setScannerOpen(false)}
                        onScan={handleQRScan}
                      />
                    </Stack>

                    {/* QR CODE SCANNER INPUT - Continuous Mode */}
                    <Paper
                      sx={{
                        p: 2.5,
                        mb: 3,
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? '#1e293b' : '#ffffff',
                          isDark ? 0.4 : 0.7
                        ),
                        border: `2px solid ${isContinuousScanning
                          ? alpha(isDark ? '#10b981' : '#059669', 0.6)
                          : alpha(isDark ? '#334155' : '#e2e8f0', isDark ? 0.2 : 0.3)
                          }`,
                        transition: 'border-color 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Scanning pulse indicator */}
                      {isContinuousScanning && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            background: isDark
                              ? 'linear-gradient(90deg, #10B981, #34D399, #10B981)'
                              : 'linear-gradient(90deg, #059669, #10B981, #059669)',
                            backgroundSize: '200% 100%',
                            animation: 'scanPulse 2s linear infinite',
                            '@keyframes scanPulse': {
                              '0%': { backgroundPosition: '0% 0%' },
                              '100%': { backgroundPosition: '200% 0%' },
                            },
                          }}
                        />
                      )}

                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <QrCode fontSize="small" />
                          {isContinuousScanning ? '🔴 LIVE — Continuous Scanning' : 'Scan QR Code (iBall BST300BT)'}
                        </Typography>

                        {isContinuousScanning && (
                          <Chip
                            label={`${scanCount} item${scanCount !== 1 ? 's' : ''} scanned`}
                            color="success"
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        )}
                      </Stack>

                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={isContinuousScanning ? 12 : 9}>
                          <TextField
                            inputRef={qrInputRef}
                            label={isContinuousScanning ? "Scanning..." : "QR Code"}
                            placeholder={
                              isContinuousScanning
                                ? "Keep scanning items one after another..."
                                : "Scan QR code or type manually..."
                            }
                            value={isContinuousScanning ? "" : scannedQRCode}
                            onChange={(e) => {
                              if (!isContinuousScanning) {
                                setScannedQRCode(e.target.value);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isContinuousScanning) {
                                  handleScanInputKeyDown(e);
                                } else {
                                  handleQRCodeAdd();
                                }
                              } else if (isContinuousScanning) {
                                handleScanInputKeyDown(e);
                              }
                            }}
                            fullWidth
                            size="small"
                            autoFocus
                            disabled={!isContinuousScanning && false}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                backdropFilter: 'blur(10px)',
                                backgroundColor: isContinuousScanning
                                  ? alpha(isDark ? '#10b981' : '#059669', isDark ? 0.15 : 0.08)
                                  : alpha(isDark ? theme.palette.background.paper : '#ffffff', isDark ? 0.2 : 0.7),
                                '&:hover': {
                                  backgroundColor: alpha(
                                    isDark ? theme.palette.background.paper : '#ffffff',
                                    isDark ? 0.3 : 0.8
                                  ),
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                color: isDark ? '#e2e8f0' : '#1e293b',
                                fontWeight: 600,
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <QrCode
                                    fontSize="small"
                                    sx={{
                                      color: isContinuousScanning
                                        ? (isDark ? '#34d399' : '#059669')
                                        : (isDark ? '#cbd5e1' : '#64748b'),
                                      animation: isContinuousScanning ? 'blink 1.2s ease-in-out infinite' : 'none',
                                      '@keyframes blink': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.3 },
                                      },
                                    }}
                                  />
                                </InputAdornment>
                              ),
                              endAdornment: !isContinuousScanning && scannedQRCode ? (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => setScannedQRCode("")} edge="end">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </Grid>

                        {!isContinuousScanning ? (
                          <>
                            <Grid item xs={12} md={3}>
                              <Button
                                fullWidth
                                startIcon={<AddIcon />}
                                variant="contained"
                                onClick={handleQRCodeAdd}
                                disabled={!scannedQRCode.trim()}
                                sx={{
                                  borderRadius: 1.5,
                                  height: '40px',
                                  background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.25)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                                  },
                                }}
                              >
                                Add from QR
                              </Button>
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                fullWidth
                                startIcon={<QrCode />}
                                variant="contained"
                                onClick={startContinuousScanning}
                                sx={{
                                  borderRadius: 1.5,
                                  height: '44px',
                                  fontWeight: 700,
                                  background: isDark
                                    ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                                    : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                  },
                                }}
                              >
                                ⚡ Start Continuous Scanning Mode
                              </Button>
                            </Grid>
                          </>
                        ) : (
                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              startIcon={<ErrorIcon />}
                              variant="contained"
                              color="error"
                              onClick={stopContinuousScanning}
                              sx={{
                                borderRadius: 1.5,
                                height: '48px',
                                fontWeight: 700,
                                fontSize: '1rem',
                                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
                                },
                              }}
                            >
                              ⏹ STOP SCANNING (Scanned: {scanCount})
                            </Button>
                          </Grid>
                        )}
                      </Grid>

                      {/* Live feedback banner */}
                      {scanFeedback && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            background: scanFeedback.type === 'success'
                              ? alpha(isDark ? '#10b981' : '#059669', isDark ? 0.25 : 0.15)
                              : alpha(isDark ? '#ef4444' : '#dc2626', isDark ? 0.25 : 0.15),
                            border: `1px solid ${scanFeedback.type === 'success'
                              ? alpha(isDark ? '#10b981' : '#059669', 0.5)
                              : alpha(isDark ? '#ef4444' : '#dc2626', 0.5)
                              }`,
                            animation: 'feedbackSlide 0.3s ease-out',
                            '@keyframes feedbackSlide': {
                              '0%': { transform: 'translateY(-10px)', opacity: 0 },
                              '100%': { transform: 'translateY(0)', opacity: 1 },
                            },
                          }}
                        >
                          {scanFeedback.type === 'success' ? (
                            <CheckCircleIcon sx={{ color: isDark ? '#34d399' : '#059669', fontSize: 20 }} />
                          ) : (
                            <ErrorIcon sx={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 20 }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: scanFeedback.type === 'success'
                                ? (isDark ? '#34d399' : '#059669')
                                : (isDark ? '#f87171' : '#dc2626'),
                            }}
                          >
                            {scanFeedback.message}
                          </Typography>
                        </Box>
                      )}

                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? '#94a3b8' : '#64748b',
                          mt: 1.5,
                          display: 'block',
                          fontSize: '0.7rem',
                        }}
                      >
                        {isContinuousScanning
                          ? '🎯 Keep scanning — each item is added automatically. Duplicates & sold items are blocked. Click STOP when done.'
                          : '💡 Click "Start Continuous Scanning Mode" for hands-free scanning, or scan once and click "Add from QR"'}
                      </Typography>
                    </Paper>

                    {/* QUICK ADD FROM STOCK */}
                    <Paper
                      sx={{
                        p: 2.5,
                        mb: 3,
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? '#1e293b' : '#ffffff',
                          isDark ? 0.4 : 0.7
                        ),
                        border: `1px solid ${alpha(
                          isDark ? '#334155' : '#e2e8f0',
                          isDark ? 0.2 : 0.3
                        )}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <InventoryIcon fontSize="small" />
                        Quick Add from Stock
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3} sx={{ width: '250px' }}>
                          <Autocomplete
                            options={itemCategories}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') return option;
                              return `${option.name} (${option.shortcut})`;
                            }}
                            value={itemCategories.find(i => i.name === manualItemName) || null}
                            onChange={(event, newValue, reason) => {
                              if (reason === "clear") {
                                setManualItemName('');
                                return;
                              }
                              if (newValue) {
                                setManualItemName(newValue.name);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Item Type"
                                size="small"
                                fullWidth
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                  <Typography variant="body2">{option.name}</Typography>
                                  <Chip
                                    label={option.shortcut}
                                    size="small"
                                    sx={{ ml: 1, fontSize: '0.7rem' }}
                                  />
                                </Box>
                              </li>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={3} sx={{ width: '250px' }}>
                          <Autocomplete
                            options={metalTypes}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') return option;
                              return `${option.name} (${option.shortcut})`;
                            }}
                            value={metalTypes.find(m => m.name === manualItemMetal) || null}
                            onChange={(event, newValue, reason) => {
                              if (reason === "clear") {
                                setManualItemMetal('');
                                return;
                              }
                              if (newValue) {
                                setManualItemMetal(newValue.name);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Metal"
                                size="small"
                                fullWidth
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                  <Typography variant="body2">{option.name}</Typography>
                                  <Chip
                                    label={option.shortcut}
                                    size="small"
                                    sx={{ ml: 1, fontSize: '0.7rem' }}
                                  />
                                </Box>
                              </li>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={3} sx={{ width: '250px' }}>
                          <TextField
                            label="Serial No."
                            value={manualSerial}
                            onChange={(e) => setManualSerial(e.target.value)}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                backdropFilter: 'blur(10px)',
                                backgroundColor: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.2 : 0.7
                                ),
                                '&:hover': {
                                  backgroundColor: alpha(
                                    isDark ? theme.palette.background.paper : '#ffffff',
                                    isDark ? 0.3 : 0.8
                                  ),
                                }
                              },
                              '& .MuiOutlinedInput-input': {
                                color: isDark ? '#e2e8f0' : '#1e293b',
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Button
                            fullWidth
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={handleAddBySerial}
                            sx={{
                              borderRadius: 1.5,
                              height: '40px',
                              width: '200px',
                              background: isDark
                                ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                                : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                              '&:hover': {
                                background: isDark
                                  ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                  : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                              }
                            }}
                          >
                            Add from Stock
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* ITEM TABLE - Modified columns */}
                    {items.length > 0 ? (
                      <TableContainer
                        component={Paper}
                        sx={{
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.2 : 0.7
                          ),
                          border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                          )}`,
                          overflow: "hidden",
                        }}
                      >
                        <Table size="small">
                          <TableHead
                            sx={{
                              backgroundColor: alpha(
                                isDark ? '#1e293b' : '#f8fafc',
                                isDark ? 0.5 : 0.9
                              ),
                            }}
                          >
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Item</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Serial No</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Metal</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Purity</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Weight</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Metal Rate</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Labour</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Price</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>{renderItemRows}</TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Paper
                        sx={{
                          p: 3,
                          textAlign: "center",
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? '#1e293b' : '#f8fafc',
                            isDark ? 0.3 : 0.7
                          ),
                          border: `1px dashed ${alpha(
                            isDark ? theme.palette.divider : '#cbd5e1',
                            isDark ? 0.2 : 0.4
                          )}`,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                          No items added yet.
                        </Typography>
                      </Paper>
                    )}
                    {transactionType === "detailed" && (
                      <Box sx={{ mt: 3 }}>
                        <TextField
                          label="Description / Notes"
                          placeholder="Enter any additional notes or description for this bill..."
                          value={transactionDescription}
                          onChange={(e) => setTransactionDescription(e.target.value)}
                          fullWidth
                          multiline
                          rows={2}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backdropFilter: 'blur(10px)',
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.2 : 0.7
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.3 : 0.8
                                ),
                              },
                              '&.Mui-focused': {
                                backgroundColor: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.3 : 0.8
                                ),
                              }
                            },
                            '& .MuiInputLabel-root': {
                              color: isDark ? '#cbd5e1' : '#64748b',
                            },
                            '& .MuiOutlinedInput-input': {
                              color: isDark ? '#e2e8f0' : '#1e293b',
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Card>
                )}

                {/* TOTAL AMOUNT (NON-ITEM) */}
                {transactionType === "total" && (
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backdropFilter: 'blur(20px)',
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : '#ffffff',
                        isDark ? 0.3 : 0.8
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    <TextField
                      label="Total Amount"
                      type="text"
                      name="totalAmount"
                      value={formik.values.totalAmount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.totalAmount && Boolean(formik.errors.totalAmount)}
                      helperText={formik.touched.totalAmount && formik.errors.totalAmount}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.2 : 0.7
                          ),
                          '&:hover': {
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.3 : 0.8
                            ),
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          color: isDark ? '#e2e8f0' : '#1e293b',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Card>
                )}

                {/* GST + DISCOUNT SECTION */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: 'blur(20px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.3 : 0.6
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : '#e2e8f0',
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <TextField
                        label="Bill Number"
                        value={isLoadingBillNo ? "Loading..." : (currentBillNo || "N/A")}
                        disabled
                        fullWidth
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&.Mui-disabled': {
                              backgroundColor: alpha(
                                isDark ? '#1e293b' : '#f1f5f9',
                                isDark ? 0.3 : 0.5
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontWeight: 600,
                          },
                          '& .MuiInputLabel-root': {
                            color: isDark ? '#cbd5e1' : '#64748b',
                          },
                        }}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={fetchLastBillNumbers}
                        disabled={isLoadingBillNo}
                        startIcon={isLoadingBillNo ? <CircularProgress size={20} /> : <RefreshIcon />}
                        sx={{
                          minWidth: '100px',
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#60a5fa' : '#3b82f6',
                            isDark ? 0.4 : 0.5
                          )}`,
                          color: isDark ? '#60a5fa' : '#3b82f6',
                          '&:hover': {
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.5 : 0.9
                            ),
                            borderColor: isDark ? '#3b82f6' : '#2563eb',
                          }
                        }}
                      >
                        Refresh
                      </Button>
                    </Stack>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 1, display: 'block' }}>
                      {showGST
                        ? `Last GST Bill No: ${gstBillNo || 0} | Next: ${currentBillNo || 1}`
                        : `Last Non-GST Bill No: ${nonGstBillNo || 0} | Next: ${currentBillNo || 1}`
                      }
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', mb: 1 }}>
                      GST Settings
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={showGST}
                          onChange={(e) => {
                            setShowGST(e.target.checked);
                            if (!e.target.checked) {
                              setGstMode("add");
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={<Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>Apply GST (3%)</Typography>}
                      sx={{ mb: 1 }}
                    />

                    {showGST && (
                      <>
                        <RadioGroup
                          row
                          value={gstMode}
                          onChange={(e) => setGstMode(e.target.value)}
                          sx={{ ml: 2, mb: 2 }}
                        >
                          <FormControlLabel
                            value="add"
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                                Add GST 3%
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            value="adjust"
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                                Adjust Labour
                              </Typography>
                            }
                          />
                        </RadioGroup>

                        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                          <InputLabel sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>GST Type</InputLabel>
                          <Select
                            value={gstType}
                            onChange={(e) => setGstType(e.target.value)}
                            label="GST Type"
                            sx={{
                              borderRadius: 2,
                              backdropFilter: 'blur(10px)',
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.2 : 0.7
                              ),
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: alpha(
                                  isDark ? theme.palette.divider : '#e2e8f0',
                                  isDark ? 0.2 : 0.3
                                ),
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: alpha(
                                  isDark ? '#60a5fa' : '#3b82f6',
                                  isDark ? 0.4 : 0.5
                                ),
                              },
                              '& .MuiSelect-select': {
                                color: isDark ? '#e2e8f0' : '#1e293b',
                              },
                            }}
                          >
                            <MenuItem value="cgst_sgst">CGST + SGST (1.5% each)</MenuItem>
                            <MenuItem value="igst">IGST (3%)</MenuItem>
                          </Select>
                        </FormControl>

                        <Box sx={{
                          mb: 2, p: 2, borderRadius: 2, background: alpha(
                            isDark ? '#1e293b' : '#f8fafc',
                            isDark ? 0.4 : 0.5
                          )
                        }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 1 }}>
                            {gstType === 'igst'
                              ? 'IGST (Integrated GST) - 3% applicable for inter-state transactions'
                              : 'CGST + SGST (Central & State GST) - 1.5% each applicable for intra-state transactions'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#60a5fa' : '#3b82f6' }}>
                            {gstType === 'igst'
                              ? `IGST Amount: ₹${calculations.igst.toFixed(2)}`
                              : `CGST: ₹${calculations.cgst.toFixed(2)} | SGST: ₹${calculations.sgst.toFixed(2)}`}
                          </Typography>
                          {gstMode === "adjust" && (
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mt: 1 }}>
                              Labour will be adjusted to keep price same after GST
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>

                  <TextField
                    label="Discount"
                    type="text"
                    name="discount"
                    value={formik.values.discount}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.2 : 0.7
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <LocalOfferIcon fontSize="small" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Card>

                {/* PAYMENT SECTION */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: 'blur(20px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.3 : 0.6
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : '#e2e8f0',
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <Typography variant="h6" sx={{
                    fontWeight: 700,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}>
                    <Payments fontSize="small" />
                    Payment Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Cash"
                        type="text"
                        name="cashPayment"
                        value={formik.values.cashPayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Online"
                        type="text"
                        name="onlinePayment"
                        value={formik.values.onlinePayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Cheque"
                        type="text"
                        name="chequePayment"
                        value={formik.values.chequePayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Other Payment"
                        type="text"
                        name="otherPayment"
                        value={formik.values.otherPayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* ADVANCE PAYMENT SWITCH */}
                  <FormControlLabel
                    sx={{ mt: 2 }}
                    control={
                      <Checkbox
                        checked={enableAdvancePayment}
                        onChange={(e) => setEnableAdvancePayment(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccountBalanceIcon fontSize="small" />
                        <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>Advance Payments</Typography>
                      </Stack>
                    }
                  />

                  {/* ADVANCE PAYMENT SECTION */}
                  <Alert
                    severity="info"
                    sx={{ mb: 2, borderRadius: 2 }}
                    icon={<InfoIcon />}
                  >
                    <Typography variant="body2">
                      <strong>Manual Advance Entry:</strong> You can add advance payments in any mode even if the customer has no advance balance.
                      {customerAdvanceBalance?.total > 0 && ` Customer has ₹${customerAdvanceBalance.total.toFixed(2)} advance balance available.`}
                      {(!customerAdvanceBalance || customerAdvanceBalance.total === 0) && " This will be treated as a manual advance payment and will not be deducted from any advance balance."}
                    </Typography>
                  </Alert>
                  {enableAdvancePayment && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                        Advance Payment Details
                      </Typography>

                      {customerAdvanceBalance && customerAdvanceBalance.total > 0 && (
                        <Alert
                          severity="info"
                          sx={{ mb: 2, borderRadius: 2 }}
                          icon={<AccountBalanceIcon />}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Available Advance Balance: ₹{customerAdvanceBalance.total.toFixed(2)}
                          </Typography>
                          <Typography variant="caption">
                            {Object.entries(customerAdvanceBalance.byMode || {})
                              .filter(([, amount]) => amount > 0)
                              .map(([mode, amount]) => `${mode}: ₹${amount.toFixed(2)}`)
                              .join(' • ')}
                          </Typography>
                        </Alert>
                      )}

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', mb: 1, display: 'block' }}>
                          Click on chips to add advance payment modes:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                          {Object.entries(paymentModes).map(([mode, enabled]) => (
                            <Chip
                              key={mode}
                              label={mode.charAt(0).toUpperCase() + mode.slice(1)}
                              clickable
                              onClick={() => {
                                const newPaymentModes = { ...paymentModes, [mode]: !enabled };
                                setPaymentModes(newPaymentModes);

                                if (!enabled) {
                                  setAdvancePayments((prev) => ({ ...prev, [mode]: "" }));
                                } else {
                                  setAdvancePayments((prev) => {
                                    const newPayments = { ...prev };
                                    delete newPayments[mode];
                                    return newPayments;
                                  });
                                }
                              }}
                              color={enabled ? "primary" : "default"}
                              variant={enabled ? "filled" : "outlined"}
                              sx={{
                                backdropFilter: 'blur(10px)',
                                backgroundColor: enabled
                                  ? alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2)
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: enabled
                                    ? alpha(theme.palette.primary.main, isDark ? 0.4 : 0.3)
                                    : alpha(theme.palette.grey[500], 0.1),
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>

                      <Grid container spacing={2}>
                        {paymentModes.cash && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Advance Cash"
                              type="text"
                              size="small"
                              fullWidth
                              value={advancePayments.cash || ""}
                              onChange={(e) => handleAdvancePaymentChange("cash", e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                endAdornment: getAvailableBalanceDisplay("cash") && (
                                  <InputAdornment position="end">
                                    {getAvailableBalanceDisplay("cash")}
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                        )}

                        {paymentModes.online && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Advance Online"
                              type="text"
                              size="small"
                              fullWidth
                              value={advancePayments.online || ""}
                              onChange={(e) => handleAdvancePaymentChange("online", e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                endAdornment: getAvailableBalanceDisplay("online") && (
                                  <InputAdornment position="end">
                                    {getAvailableBalanceDisplay("online")}
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                        )}

                        {paymentModes.cheque && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Advance Cheque"
                              type="text"
                              size="small"
                              fullWidth
                              value={advancePayments.cheque || ""}
                              onChange={(e) => handleAdvancePaymentChange("cheque", e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                endAdornment: getAvailableBalanceDisplay("cheque") && (
                                  <InputAdornment position="end">
                                    {getAvailableBalanceDisplay("cheque")}
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                        )}

                        {paymentModes.oldGold && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Advance Old Gold"
                              type="text"
                              size="small"
                              fullWidth
                              value={advancePayments.oldGold || ""}
                              onChange={(e) => handleAdvancePaymentChange("oldGold", e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                endAdornment: getAvailableBalanceDisplay("oldGold") && (
                                  <InputAdornment position="end">
                                    {getAvailableBalanceDisplay("oldGold")}
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                        )}

                        {paymentModes.oldSilver && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Advance Old Silver"
                              type="text"
                              size="small"
                              fullWidth
                              value={advancePayments.oldSilver || ""}
                              onChange={(e) => handleAdvancePaymentChange("oldSilver", e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                endAdornment: getAvailableBalanceDisplay("oldSilver") && (
                                  <InputAdornment position="end">
                                    {getAvailableBalanceDisplay("oldSilver")}
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>

                      <Box sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontWeight: 600 }}>Total Advance Used:</Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'primary.main' }}>
                            ₹{advanceTotal.toFixed(2)}
                          </Typography>
                        </Stack>
                      </Box>

                      {customerAdvanceBalance && customerAdvanceBalance.total > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const maxAdvance = Math.min(
                                customerAdvanceBalance.total,
                                calculations.grandTotal - (totalPayments - advanceTotal)
                              );

                              if (maxAdvance <= 0) {
                                setSnackbar({
                                  open: true,
                                  message: "Cannot apply more advance than bill amount",
                                  severity: 'warning'
                                });
                                return;
                              }

                              const newAdvancePayments = { ...advancePayments };
                              let remaining = maxAdvance;

                              const newPaymentModes = { ...paymentModes };
                              Object.keys(customerAdvanceBalance.byMode || {}).forEach(mode => {
                                if (customerAdvanceBalance.byMode[mode] > 0) {
                                  newPaymentModes[mode] = true;
                                }
                              });
                              setPaymentModes(newPaymentModes);

                              Object.keys(customerAdvanceBalance.byMode || {}).forEach(mode => {
                                if (remaining > 0 && customerAdvanceBalance.byMode[mode] > 0) {
                                  const available = customerAdvanceBalance.byMode[mode];
                                  const toUse = Math.min(available, remaining);
                                  if (toUse > 0) {
                                    newAdvancePayments[mode] = toUse.toString();
                                    remaining -= toUse;
                                  }
                                }
                              });

                              setAdvancePayments(newAdvancePayments);
                              setSnackbar({
                                open: true,
                                message: `Applied ₹${maxAdvance.toFixed(2)} from advance balance`,
                                severity: 'success'
                              });
                            }}
                            sx={{ mr: 1 }}
                          >
                            Apply Max Available
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                              const resetPayments = {};
                              Object.keys(advancePayments).forEach(key => {
                                resetPayments[key] = "0";
                              });
                              setAdvancePayments(resetPayments);
                            }}
                          >
                            Clear All
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* FINE PAYMENT SECTION */}
                  {hasFineBasedItems && (
                    <Box sx={{ mt: 3 }}>
                      <Divider sx={{ mb: 2 }} />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={enableFinePayment}
                            onChange={(e) => {
                              setEnableFinePayment(e.target.checked);
                              if (!e.target.checked) {
                                setFinePayments([]);
                              }
                            }}
                            color="secondary"
                          />
                        }
                        label={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <ScaleIcon fontSize="small" />
                            <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontWeight: 600 }}>
                              Fine Payment (Metal)
                            </Typography>
                          </Stack>
                        }
                      />

                      {enableFinePayment && (
                        <Box sx={{ mt: 2 }}>
                          {/* Fine Summary */}
                          <Alert
                            severity="info"
                            sx={{ mb: 2, borderRadius: 2 }}
                            icon={<ScaleIcon />}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                  Fine Required
                                </Typography>
                                <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                                  {totalFineRequired.toFixed(3)} g
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                  Fine Paid
                                </Typography>
                                <Typography sx={{ fontWeight: 700, color: isDark ? '#34d399' : '#059669' }}>
                                  {totalFinePaid.toFixed(3)} g
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                  Balance Fine
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: balanceFine > 0
                                      ? isDark ? '#f87171' : '#dc2626'
                                      : isDark ? '#34d399' : '#059669',
                                  }}
                                >
                                  {balanceFine.toFixed(3)} g
                                </Typography>
                              </Grid>
                            </Grid>
                          </Alert>

                          {/* Fine Payment Mode Chips */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', mb: 1, display: 'block' }}>
                              Add fine payment entries by metal type:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                              <Chip
                                label="+ Silver"
                                clickable
                                onClick={() => handleAddFinePayment('silver')}
                                color="default"
                                variant="outlined"
                                sx={{
                                  backdropFilter: 'blur(10px)',
                                  backgroundColor: alpha(theme.palette.grey[500], 0.1),
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.grey[500], 0.2),
                                  }
                                }}
                              />
                              <Chip
                                label="+ Gold"
                                clickable
                                onClick={() => handleAddFinePayment('gold')}
                                color="warning"
                                variant="outlined"
                                sx={{
                                  backdropFilter: 'blur(10px)',
                                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.warning.main, 0.2),
                                  }
                                }}
                              />
                              {finePayments.length > 0 && (
                                <Chip
                                  label="Clear All"
                                  clickable
                                  onClick={handleClearFinePayments}
                                  color="error"
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                            </Stack>
                          </Box>

                          {/* Fine Payment Entries */}
                          {finePayments.length > 0 && (
                            <Paper
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                backdropFilter: 'blur(10px)',
                                backgroundColor: alpha(
                                  isDark ? '#1e293b' : '#f8fafc',
                                  isDark ? 0.4 : 0.6
                                ),
                                border: `1px solid ${alpha(
                                  isDark ? theme.palette.divider : '#e2e8f0',
                                  isDark ? 0.2 : 0.3
                                )}`,
                              }}
                            >
                              <Stack spacing={2}>
                                {finePayments.map((payment, index) => (
                                  <Grid container spacing={2} alignItems="center" key={payment.id}>
                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        label="Metal"
                                        value={payment.metal}
                                        size="small"
                                        fullWidth
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5,
                                            backgroundColor: alpha(
                                              isDark ? theme.palette.background.paper : '#ffffff',
                                              isDark ? 0.2 : 0.7
                                            ),
                                          },
                                          '& .MuiOutlinedInput-input': {
                                            color: isDark ? '#e2e8f0' : '#1e293b',
                                            textTransform: 'capitalize',
                                            fontWeight: 600,
                                          },
                                        }}
                                      />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        label="Weight (g)"
                                        type="text"
                                        value={payment.weight}
                                        onChange={(e) => handleUpdateFinePayment(payment.id, 'weight', e.target.value)}
                                        size="small"
                                        fullWidth
                                        placeholder="0.000"
                                        InputProps={{
                                          endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                        }}
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5,
                                            backgroundColor: alpha(
                                              isDark ? theme.palette.background.paper : '#ffffff',
                                              isDark ? 0.2 : 0.7
                                            ),
                                          },
                                          '& .MuiOutlinedInput-input': {
                                            color: isDark ? '#e2e8f0' : '#1e293b',
                                          },
                                        }}
                                      />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        label="Purity (%)"
                                        type="text"
                                        value={payment.purity}
                                        onChange={(e) => handleUpdateFinePayment(payment.id, 'purity', e.target.value)}
                                        size="small"
                                        fullWidth
                                        placeholder="100"
                                        InputProps={{
                                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5,
                                            backgroundColor: alpha(
                                              isDark ? theme.palette.background.paper : '#ffffff',
                                              isDark ? 0.2 : 0.7
                                            ),
                                          },
                                          '& .MuiOutlinedInput-input': {
                                            color: isDark ? '#e2e8f0' : '#1e293b',
                                          },
                                        }}
                                      />
                                    </Grid>

                                    <Grid item xs={12} md={2}>
                                      <Box
                                        sx={{
                                          p: 1.5,
                                          borderRadius: 1,
                                          background: alpha(theme.palette.success.main, 0.1),
                                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                          textAlign: 'center',
                                        }}
                                      >
                                        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block' }}>
                                          Fine
                                        </Typography>
                                        <Typography sx={{ fontWeight: 700, color: isDark ? '#34d399' : '#059669', fontSize: '0.9rem' }}>
                                          {((parseFloat(payment.weight) || 0) * (parseFloat(payment.purity) || 0) / 100).toFixed(3)} g
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} md={1}>
                                      <IconButton
                                        onClick={() => handleRemoveFinePayment(payment.id)}
                                        size="small"
                                        sx={{
                                          borderRadius: 2,
                                          backgroundColor: alpha(theme.palette.error.main, isDark ? 0.2 : 0.1),
                                          color: theme.palette.error.main,
                                          '&:hover': {
                                            backgroundColor: alpha(theme.palette.error.main, isDark ? 0.3 : 0.15),
                                          },
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Grid>
                                  </Grid>
                                ))}
                              </Stack>
                            </Paper>
                          )}

                          {/* Balance Fine Indicator */}
                          {finePayments.length > 0 && (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: 1,
                                background: balanceFine > 0
                                  ? alpha(theme.palette.error.main, 0.1)
                                  : alpha(theme.palette.success.main, 0.1),
                                border: `1px solid ${balanceFine > 0
                                  ? alpha(theme.palette.error.main, 0.2)
                                  : alpha(theme.palette.success.main, 0.2)
                                  }`,
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                                  Balance Fine:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    color: balanceFine > 0
                                      ? isDark ? '#f87171' : '#dc2626'
                                      : isDark ? '#34d399' : '#059669',
                                  }}
                                >
                                  {balanceFine.toFixed(3)} g
                                </Typography>
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  <TextField
                    label="Total Payment Done"
                    fullWidth
                    margin="normal"
                    size="small"
                    value={formik.values.paymentDone}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />
                </Card>
              </Stack>
            </Grid>

            {/* RIGHT COLUMN — SUMMARY + ACTION BUTTONS */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Stack
                spacing={3}
                sx={{
                  position: "sticky",
                  top: 24,
                }}
              >
                {/* SUMMARY PANEL */}
                <Card
                  sx={{
                    p: 0,
                    borderRadius: 3,
                    backdropFilter: 'blur(20px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.3 : 0.3
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : '#e2e8f0',
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: isDark
                        ? 'linear-gradient(90deg, #60A5FA 0%, #3B82F6 50%, #1D4ED8 100%)'
                        : 'linear-gradient(90deg, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)',
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      pb: 2,
                      background: alpha(
                        isDark ? '#1e293b' : '#f8fafc',
                        isDark ? 0.4 : 0.5
                      ),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          background: isDark
                            ? 'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)'
                            : 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isDark
                              ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                              : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                            color: 'white',
                          }}
                        >
                          <Summarize fontSize="small" />
                        </Box>
                        Transaction Summary
                      </Typography>

                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          background: alpha(
                            isDark ? '#10b981' : '#10b981',
                            isDark ? 0.2 : 0.15
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#10b981' : '#059669',
                            isDark ? 0.3 : 0.2
                          )}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? '#34d399' : '#059669',
                            fontSize: '0.7rem',
                          }}
                        >
                          {showGST ? 'GST INVOICE' : 'CASH MEMO'}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? '#94a3b8' : '#64748b',
                        display: 'block',
                        pl: 5.5,
                      }}
                    >
                      Real-time calculations • Auto-updating
                    </Typography>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Calculate fontSize="small" />
                          Amount Breakdown
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                            Subtotal
                          </Typography>
                          <Typography sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                            ₹{calculations.subtotal.toFixed(2)}
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            height: '4px',
                            borderRadius: '2px',
                            background: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.3 : 0.2
                            ),
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: '100%',
                              background: isDark
                                ? 'linear-gradient(90deg, #60A5FA 0%, #3B82F6 100%)'
                                : 'linear-gradient(90deg, #2563EB 0%, #1D4ED8 100%)',
                              borderRadius: '2px',
                            }}
                          />
                        </Box>
                      </Box>

                      {showGST && (
                        <>
                          {gstType === 'igst' ? (
                            <Box sx={{ mb: 2 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                                  IGST (3%)
                                </Typography>
                                <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                                  ₹{calculations.igst.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Box
                                sx={{
                                  height: '3px',
                                  borderRadius: '1.5px',
                                  background: alpha(
                                    isDark ? '#8b5cf6' : '#8b5cf6',
                                    isDark ? 0.3 : 0.2
                                  ),
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    width: '100%',
                                    background: isDark
                                      ? 'linear-gradient(90deg, #A78BFA 0%, #8B5CF6 100%)'
                                      : 'linear-gradient(90deg, #8B5CF6 0%, #7C3AED 100%)',
                                    borderRadius: '1.5px',
                                  }}
                                />
                              </Box>
                            </Box>
                          ) : (
                            <>
                              <Box sx={{ mb: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                                    CGST (1.5%)
                                  </Typography>
                                  <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                                    ₹{calculations.cgst.toFixed(2)}
                                  </Typography>
                                </Stack>
                                <Box
                                  sx={{
                                    height: '3px',
                                    borderRadius: '1.5px',
                                    background: alpha(
                                      isDark ? '#34d399' : '#10b981',
                                      isDark ? 0.3 : 0.2
                                    ),
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      background: isDark
                                        ? 'linear-gradient(90deg, #34D399 0%, #10B981 100%)'
                                        : 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                                      borderRadius: '1.5px',
                                    }}
                                  />
                                </Box>
                              </Box>

                              <Box sx={{ mb: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                                    SGST (1.5%)
                                  </Typography>
                                  <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                                    ₹{calculations.sgst.toFixed(2)}
                                  </Typography>
                                </Stack>
                                <Box
                                  sx={{
                                    height: '3px',
                                    borderRadius: '1.5px',
                                    background: alpha(
                                      isDark ? '#34d399' : '#10b981',
                                      isDark ? 0.3 : 0.2
                                    ),
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      background: isDark
                                        ? 'linear-gradient(90deg, #34D399 0%, #10B981 100%)'
                                        : 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                                      borderRadius: '1.5px',
                                    }}
                                  />
                                </Box>
                              </Box>
                            </>
                          )}
                        </>
                      )}

                      {formik.values.discount > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                              Discount
                            </Typography>
                            <Typography sx={{ fontWeight: 600, color: 'error.main', fontSize: '0.9rem' }}>
                              -₹{parseFloat(formik.values.discount).toFixed(2)}
                            </Typography>
                          </Stack>
                          <Box
                            sx={{
                              height: '3px',
                              borderRadius: '1.5px',
                              background: alpha(
                                isDark ? theme.palette.error.main : theme.palette.error.main,
                                isDark ? 0.3 : 0.2
                              ),
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: '100%',
                                background: isDark
                                  ? 'linear-gradient(90deg, #F87171 0%, #EF4444 100%)'
                                  : 'linear-gradient(90deg, #EF4444 0%, #DC2626 100%)',
                                borderRadius: '1.5px',
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          background: isDark
                            ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.08) 100%)',
                          border: `1px solid ${alpha(
                            isDark ? '#3b82f6' : '#2563eb',
                            isDark ? 0.2 : 0.15
                          )}`,
                          mt: 2,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                            Grand Total
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: '1.25rem',
                              background: isDark
                                ? 'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)'
                                : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                            }}
                          >
                            ₹{calculations.grandTotal.toFixed(2)}
                          </Typography>
                        </Stack>
                      </Box>
                      {transactionType === "detailed" && calculations.totalNetWeight > 0 && (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            background: isDark
                              ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.08) 100%)',
                            border: `1px solid ${alpha(
                              isDark ? '#8b5cf6' : '#7c3aed',
                              isDark ? 0.2 : 0.15
                            )}`,
                            mt: 1.5,
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ScaleIcon fontSize="small" sx={{ color: isDark ? '#a78bfa' : '#8b5cf6' }} />
                              Total Net Weight
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: '1.15rem',
                                color: isDark ? '#a78bfa' : '#7c3aed',
                              }}
                            >
                              {calculations.totalNetWeight.toFixed(3)} g
                            </Typography>
                          </Stack>

                          {/* Add this in the Summary Panel after Total Net Weight section */}
                          {hasFineBasedItems && enableFinePayment && (
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 1,
                                background: isDark
                                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)'
                                  : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%)',
                                border: `1px solid ${alpha(
                                  isDark ? '#10b981' : '#059669',
                                  isDark ? 0.2 : 0.15
                                )}`,
                                mt: 1.5,
                              }}
                            >
                              <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between">
                                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                    Fine Required
                                  </Typography>
                                  <Typography sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.85rem' }}>
                                    {calculations.fineRequired.toFixed(3)} g
                                  </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                    Fine Paid
                                  </Typography>
                                  <Typography sx={{ fontWeight: 600, color: isDark ? '#34d399' : '#059669', fontSize: '0.85rem' }}>
                                    {calculations.finePaid.toFixed(3)} g
                                  </Typography>
                                </Stack>
                                <Divider sx={{ my: 0.5 }} />
                                <Stack direction="row" justifyContent="space-between">
                                  <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.85rem' }}>
                                    Balance Fine
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 800,
                                      fontSize: '1rem',
                                      color: calculations.fineBalance > 0
                                        ? isDark ? '#f87171' : '#dc2626'
                                        : isDark ? '#34d399' : '#059669',
                                    }}
                                  >
                                    {calculations.fineBalance.toFixed(3)} g
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Payments fontSize="small" />
                          Payment Summary
                        </Typography>
                      </Box>

                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? '#10b981' : '#10b981',
                                isDark ? 0.15 : 0.1
                              ),
                              border: `1px solid ${alpha(
                                isDark ? '#10b981' : '#059669',
                                isDark ? 0.2 : 0.15
                              )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              Paid
                            </Typography>
                            <Typography sx={{ fontWeight: 700, color: isDark ? '#34d399' : '#059669', fontSize: '1rem' }}>
                              ₹{formik.values.paymentDone.toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={6}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: pendingAmount > 0
                                ? alpha(
                                  isDark ? theme.palette.error.main : theme.palette.error.main,
                                  isDark ? 0.15 : 0.1
                                )
                                : alpha(
                                  isDark ? '#10b981' : '#10b981',
                                  isDark ? 0.15 : 0.1
                                ),
                              border: `1px solid ${pendingAmount > 0
                                ? alpha(
                                  isDark ? theme.palette.error.main : theme.palette.error.main,
                                  isDark ? 0.2 : 0.15
                                )
                                : alpha(
                                  isDark ? '#10b981' : '#059669',
                                  isDark ? 0.2 : 0.15
                                )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              {pendingAmount > 0 ? 'Pending' : 'Balance'}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: pendingAmount > 0
                                  ? isDark ? '#f87171' : '#dc2626'
                                  : isDark ? '#34d399' : '#059669',
                                fontSize: '1rem',
                              }}
                            >
                              ₹{pendingAmount.toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <InfoIcon fontSize="small" />
                          Quick Stats
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? '#1e293b' : '#f8fafc',
                                isDark ? 0.4 : 0.6
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : '#e2e8f0',
                                isDark ? 0.2 : 0.3
                              )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              Items
                            </Typography>
                            <Typography sx={{ fontWeight: 800, color: isDark ? '#60a5fa' : '#3b82f6', fontSize: '1.2rem' }}>
                              {items.length}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? '#1e293b' : '#f8fafc',
                                isDark ? 0.4 : 0.6
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : '#e2e8f0',
                                isDark ? 0.2 : 0.3
                              )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              GST
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: showGST
                                  ? isDark ? '#34d399' : '#059669'
                                  : isDark ? '#94a3b8' : '#64748b',
                                fontSize: '1.2rem',
                              }}
                            >
                              {showGST ? gstType === 'igst' ? 'IGST' : 'CGST/SGST' : 'OFF'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? '#1e293b' : '#f8fafc',
                                isDark ? 0.4 : 0.6
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : '#e2e8f0',
                                isDark ? 0.2 : 0.3
                              )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              Mode
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: showGST && gstMode === 'adjust'
                                  ? isDark ? '#f59e0b' : '#d97706'
                                  : isDark ? '#94a3b8' : '#64748b',
                                fontSize: '1.2rem',
                              }}
                            >
                              {showGST ? (gstMode === 'add' ? 'ADD' : 'ADJ') : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Card>

                {/* Pending Adjustments Summary */}
                {Object.keys(weightAdjustments).length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: isDark ? "#e2e8f0" : "#1e293b",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ScaleIcon fontSize="small" />
                        Pending Weight Adjustments
                      </Typography>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      {Object.keys(weightAdjustments).length} item(s) have
                      weight adjustments pending. These adjustments will be
                      applied when the transaction is saved.
                    </Alert>

                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell>Original Weight</TableCell>
                            <TableCell>New Weight</TableCell>
                            <TableCell>Adjustment</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.values(weightAdjustments).map(
                            ({ item, adjustment }) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>
                                  {item?.originalWeight}g
                                </TableCell>
                                <TableCell>{item?.newWeight}g</TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      adjustment.action === "ignore"
                                        ? "Ignored"
                                        : adjustment.action === "gross_stock"
                                          ? `Gross Stock (${adjustment.metal})`
                                          : adjustment.action === "another_item"
                                            ? `Another Item (${adjustment.item?.name})`
                                            : "New Item Created"
                                    }
                                    size="small"
                                    color={
                                      adjustment.action === "ignore"
                                        ? "default"
                                        : "success"
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleManualWeightAdjustment(item)
                                    }
                                    sx={{
                                      borderRadius: 1,
                                      backgroundColor: alpha(
                                        theme.palette.warning.main,
                                        0.1,
                                      ),
                                      color: theme.palette.warning.main,
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* ACTION BUTTONS */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: 'blur(20px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.3 : 0.6
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : '#e2e8f0',
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <form onSubmit={formik.handleSubmit}>
                    <Stack direction="row" spacing={2}>
                      {!isTransactionSaved && (
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          size="large"
                          fullWidth
                          sx={{
                            py: 1.4,
                            fontWeight: 700,
                            borderRadius: 2,
                            background: isDark
                              ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                              : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                            '&:hover': {
                              background: isDark
                                ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                                : 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                            }
                          }}
                        >
                          Save Transaction
                        </Button>
                      )}

                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        size="large"
                        fullWidth
                        disabled={items.length === 0 && transactionType === "detailed"}
                        onClick={handlePrintBill}
                        sx={{
                          py: 1.4,
                          fontWeight: 600,
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1.5px solid ${alpha(
                            isDark ? '#8b5cf6' : '#8b5cf6',
                            isDark ? 0.4 : 0.5
                          )}`,
                          color: isDark ? '#a78bfa' : '#8b5cf6',
                        }}
                      >
                        Print Bill
                      </Button>

                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        size="large"
                        fullWidth
                        onClick={resetForm}
                        sx={{
                          py: 1.4,
                          fontWeight: 600,
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1.5px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.3 : 0.4
                          )}`,
                          color: isDark ? '#e2e8f0' : '#475569',
                        }}
                      >
                        Reset Form
                      </Button>
                    </Stack>
                  </form>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <WeightAdjustmentDialog
          open={adjustmentDialogOpen}
          onClose={() => {
            setAdjustmentDialogOpen(false);
            setCurrentAdjustmentItem(null);
          }}
          item={currentAdjustmentItem}
          originalWeight={
            currentAdjustmentItem?.originalWeight ||
            currentAdjustmentItem?.weight ||
            0
          }
          newWeight={
            currentAdjustmentItem?.newWeight ||
            currentAdjustmentItem?.weight ||
            0
          }
          weightDifference={Math.abs(
            (currentAdjustmentItem?.newWeight ||
              currentAdjustmentItem?.weight ||
              0) -
            (currentAdjustmentItem?.originalWeight ||
              currentAdjustmentItem?.weight ||
              0),
          )}
          isWeightIncreased={
            (currentAdjustmentItem?.newWeight || 0) >
            (currentAdjustmentItem?.originalWeight || 0)
          }
          onAdjustmentComplete={handleAdjustmentComplete}
          onSkip={handleSkipAdjustment}
        />

        <WeightEditDialog
          open={weightEditDialogOpen}
          onClose={() => {
            setWeightEditDialogOpen(false);
            setEditingItemWeight(null);
          }}
          item={editingItemWeight}
          onConfirm={handleWeightChangeConfirm}
        />

        <SimpleDeleteDialog
          open={
            deleteConfirmDialog.open && !deleteConfirmDialog.hasWeightAdjustment
          }
          onClose={() =>
            setDeleteConfirmDialog({
              open: false,
              itemToDelete: null,
              hasWeightAdjustment: false,
            })
          }
          onConfirm={confirmDeleteItem}
          item={deleteConfirmDialog.itemToDelete}
        />

        <WeightAdjustmentDeleteDialog
          open={
            deleteConfirmDialog.open && deleteConfirmDialog.hasWeightAdjustment
          }
          onClose={() =>
            setDeleteConfirmDialog({
              open: false,
              itemToDelete: null,
              hasWeightAdjustment: false,
            })
          }
          onConfirm={confirmDeleteItem}
          item={deleteConfirmDialog.itemToDelete}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: "100%",
              borderRadius: 2,
              boxShadow: theme.shadows[3],
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <AdvancePaymentSelectorDialog
          open={advanceSelectorOpen}
          onClose={() => {
            setAdvanceSelectorOpen(false);
            if (selectedCustomer?.id) {
              hasShownAdvanceDialogRef.current = selectedCustomer.id;
            }
          }}
          customerId={selectedCustomer?.id}
          customerName={selectedCustomer?.name}
          advanceBalance={customerAdvanceBalance}
          billTotal={calculations.grandTotal}
          onUseAdvance={(usageData) => {
            handleUseAdvance(usageData);
            setAdvanceSelectorOpen(false);
            if (selectedCustomer?.id) {
              hasShownAdvanceDialogRef.current = selectedCustomer.id;
            }
          }}
          onSkip={() => {
            setAdvanceSelectorOpen(false);
            if (selectedCustomer?.id) {
              hasShownAdvanceDialogRef.current = selectedCustomer.id;
            }
          }}
        />

        <CustomAlertDialog
          open={customAlert.open}
          onClose={() => setCustomAlert({ ...customAlert, open: false })}
          title={customAlert.title}
          message={customAlert.message}
          severity={customAlert.severity}
          onConfirm={customAlert.onConfirm}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CustomerTransaction;